import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { Modal } from 'react-bootstrap'
import {
    createManualExam,
    getExamAssignments,
    getExamQuestionPool,
    getExamQuestionPoolDepartments,
    addExamQuestions,
    reorderExamQuestions,
    removeExamQuestion,
    getManualExamSettings,
    updateManualExamBooklets,
    generateManualExamBooklets,
    getManualExamAnswerKeys,
    downloadManualExamPdf,
    updateExamQuestionPoints,
    updateManualExamStatus,
    getMyQuestionTags,
    getAutomaticExamName,
    getManualExamCourseShareInfo,
    uploadManualExamMaterial,
    deleteManualExamMaterial,
} from './core/_requests'
import {
    ExamQuestionAssignment,
    ExamQuestionAssignmentOption,
    ExamQuestionAssignmentGroup,
    ExamQuestionAssignmentsResponse,
    ExamQuestionPoolLearningObjective,
    ExamQuestionPoolQuestion,
    ExamQuestionPoolResponse,
    AddExamQuestionsPayload,
    CreateManualExamPayload,
    ReorderExamQuestionsPayload,
    ManualExamAnswerKeyItem,
    UpdateExamQuestionPointsPayload,
    ManualExamStatus,
    ManualExamMeasurementCenterHistoryItem,
    QuestionTagSummary,
    ManualExamBookletLayoutSettings,
    createDefaultManualExamLayoutSettings,
    normalizeManualExamLayoutSettings,
    ManualExamCourseShareInfo,
    ManualExamCourseShareCourse,
} from './core/_models'
import { getExamTypes, getExamById } from '../list/core/_requests'
import { getInstructorSemesterCourseById } from '../../my-course/list/core/_requests'
import type { Model as SemesterCourseModel } from '../../my-course/list/core/_models'
import { Model as ExamModel } from '../list/core/_models'
import { fetchTenantSettings } from '../../setting/list/core/_requests'
import ManualExamLayoutSettingsEditor from './ManualExamLayoutSettingsEditor'
import ManualExamLayoutPreview from './ManualExamLayoutPreview'
import {isReferenceExam, showReferenceExamWarning} from '../list/core/referenceExamUtils'
import { deriveDifficultyInfo } from '../../../utils/difficulty'
import {
    EXAM_DELIVERY_METHOD_OPTIONS,
    ExamDeliveryMethod,
} from '../core/examDeliveryMethod'

const DEFAULT_BOOKLET_CODES = ['A', 'B', 'C', 'D']
const AUTO_CALCULATE_POINTS_STORAGE_KEY = 'manual_exam_auto_calculate_points'

const sanitizeBookletCodes = (codes: string[]): string[] => {
    const normalized = (Array.isArray(codes) ? codes : [])
        .map((code) => (typeof code === 'string' ? code.trim() : ''))
        .filter((code) => code.length > 0)

    if (!normalized.length) {
        return [DEFAULT_BOOKLET_CODES[0]]
    }

    return normalized.slice(0, DEFAULT_BOOKLET_CODES.length)
}

const ensureBookletCount = (codes: string[], desiredCount: number): string[] => {
    const safeDesiredCount = Math.min(
        Math.max(Math.floor(Number.isFinite(desiredCount) ? desiredCount : 1), 1),
        DEFAULT_BOOKLET_CODES.length
    )
    const sanitized = sanitizeBookletCodes(codes)
    const next = sanitized.slice(0, safeDesiredCount)
    if (next.length >= safeDesiredCount) {
        return next
    }

    const available = DEFAULT_BOOKLET_CODES.filter((code) =>
        !next.some((existing) => existing.toLowerCase() === code.toLowerCase())
    )

    const filled = [...next]
    while (filled.length < safeDesiredCount) {
        filled.push(available.shift() || `Kod${filled.length + 1}`)
    }

    return filled
}

const MANUAL_EXAM_STATUS_OPTIONS: Array<{ value: ManualExamStatus; label: string }> = [
    { value: 'Preparing', label: 'Hazırlanıyor' },
    { value: 'SentToMeasurementCenter', label: 'Ölçme Merkezine Gönderildi' },
    { value: 'PrintingInProgress', label: 'Basım Aşamasında' },
    { value: 'PrintingCompleted', label: 'Basım Tamamlandı' },
    { value: 'ExamCompleted', label: 'Sınav Tamamlandı' },
    { value: 'OpticalReadingCompleted', label: 'Optik Okuma Süreci Tamamlandı' },
    { value: 'GradingCompleted', label: 'Not Girişi Tamamlandı' },
]

const MANUAL_EXAM_STATUS_LABELS: Record<ManualExamStatus, string> = MANUAL_EXAM_STATUS_OPTIONS.reduce(
    (acc, option) => ({ ...acc, [option.value]: option.label }),
    {} as Record<ManualExamStatus, string>
)

const MANUAL_EXAM_STATUS_BADGES: Record<ManualExamStatus, string> = {
    Preparing: 'badge badge-light-warning text-dark',
    SentToMeasurementCenter: 'badge badge-light-primary',
    PrintingInProgress: 'badge badge-light-info',
    PrintingCompleted: 'badge badge-light-success',
    ExamCompleted: 'badge badge-light-dark',
    OpticalReadingCompleted: 'badge badge-light-info',
    GradingCompleted: 'badge badge-light-success',
}

const IMAGE_WRAPPER_CLASS = 'manual-exam-image-wrapper'
const IMAGE_CLASS = 'manual-exam-image'
const IMAGE_CLASS_REGEX = new RegExp(`\\b${IMAGE_CLASS}\\b`, 'i')
const normalizeDifficultyDetails = (
    rawLabel?: string | null,
    rawNumeric?: any
): { label: string | null; value: number | null } => {
    const derived = deriveDifficultyInfo(rawNumeric)
    const label =
        (typeof rawLabel === 'string' && rawLabel.trim().length > 0 ? rawLabel : null) ??
        (derived.level ? `Seviye ${derived.level}` : derived.label)

    const value =
        typeof rawNumeric === 'number' && rawNumeric > 1
            ? rawNumeric
            : derived.level ?? derived.rawScore ?? null

    return { label, value }
}
const IMAGE_WRAPPER_STYLE = 'display:inline-block;max-width:90%;margin-left:4pt;'
const IMAGE_STYLE = 'max-width:100%;height:auto;'
const getImageSafeQuestionName = (question: any): string => {
    if (typeof question?.questionName === 'string' && /<img\b/i.test(question.questionName)) {
        return question.questionName
    }

    if (typeof question?.questionNameWithoutImages === 'string' && question.questionNameWithoutImages.length > 0) {
        return question.questionNameWithoutImages
    }

    return question?.questionName || ''
}

const getImageSafeOptionName = (option: any): string => {
    if (typeof option?.name === 'string' && /<img\b/i.test(option.name)) {
        return option.name
    }

    if (typeof option?.nameWithoutImages === 'string' && option.nameWithoutImages.length > 0) {
        return option.nameWithoutImages
    }

    return option?.name || ''
}

const wrapManualExamImages = (html?: string | null): string => {
    if (!html) {
        return ''
    }

    return html.replace(/<img\b[^>]*>/gi, (imgTag) => {
        if (/data-manual-img-wrapper/i.test(imgTag) || IMAGE_CLASS_REGEX.test(imgTag)) {
            return imgTag
        }

        let updatedTag = imgTag
        const classMatch = updatedTag.match(/class\s*=\s*(['"])(.*?)\1/i)
        if (classMatch) {
            const quote = classMatch[1]
            const classes = classMatch[2] || ''
            const tokens = classes.split(/\s+/).filter((token) => token.length > 0)
            const hasImageClass = tokens.some((token) => token.toLowerCase() === IMAGE_CLASS)
            if (!hasImageClass) {
                const replacement = `class=${quote}${tokens.length ? `${tokens.join(' ')} ${IMAGE_CLASS}` : IMAGE_CLASS}${quote}`
                updatedTag = updatedTag.replace(classMatch[0], replacement)
            }
        } else {
            const closeIndex = updatedTag.indexOf('>')
            if (closeIndex >= 0) {
                updatedTag = `${updatedTag.slice(0, closeIndex)} class="${IMAGE_CLASS}"${updatedTag.slice(closeIndex)}`
            }
        }

        const styleMatch = updatedTag.match(/style\s*=\s*(['"])(.*?)\1/i)
        if (styleMatch) {
            const quote = styleMatch[1]
            const styles = styleMatch[2] || ''
            const trimmedStyles = styles.trim()
            const needsSemicolon = trimmedStyles.length > 0 && !trimmedStyles.endsWith(';')
            const appendedStyles = `${trimmedStyles}${needsSemicolon ? ';' : ''}${IMAGE_STYLE}`
            updatedTag = updatedTag.replace(styleMatch[0], `style=${quote}${appendedStyles}${quote}`)
        } else {
            const closeIndex = updatedTag.indexOf('>')
            if (closeIndex >= 0) {
                updatedTag = `${updatedTag.slice(0, closeIndex)} style="${IMAGE_STYLE}"${updatedTag.slice(closeIndex)}`
            }
        }

        if (!/data-manual-img-wrapper/i.test(updatedTag)) {
            updatedTag = updatedTag.replace(/<img/i, (match) => `${match} data-manual-img-wrapper="true"`)
        }

        return `<span class="${IMAGE_WRAPPER_CLASS}" style="${IMAGE_WRAPPER_STYLE}">${updatedTag}</span>`
    })
}

const sanitizeFileNamePart = (value?: string | null): string => {
    if (typeof value !== 'string') {
        return ''
    }

    return value
        .trim()
        .replace(/\s+/g, '_')
        .replace(/[<>:"/\\|?*\u0000-\u001F]+/g, '')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '')
}

const buildManualExamFileBaseName = (courseName?: string | null, examName?: string | null): string => {
    const parts = [sanitizeFileNamePart(courseName), sanitizeFileNamePart(examName)].filter((part) => part.length > 0)
    return parts.length > 0 ? parts.join('_') : 'sinav'
}

const OPEN_ENDED_PLACEHOLDER_LINES = 3
const OPEN_ENDED_LINE_STYLE: React.CSSProperties = {
    borderBottom: '1px dashed rgba(0, 0, 0, 0.2)',
    height: '1.5rem',
}

const renderOpenEndedPlaceholder = (extraClassName?: string): React.ReactElement => {
    const classes = ['manual-exam-open-ended-placeholder', 'd-flex', 'flex-column', 'gap-2']
    if (extraClassName) {
        classes.push(extraClassName)
    }

    return (
        <div className={classes.join(' ')}>
            {Array.from({ length: OPEN_ENDED_PLACEHOLDER_LINES }).map((_, index) => (
                <div
                    key={`open-ended-line-${index}`}
                    className='manual-exam-open-ended-line'
                    style={OPEN_ENDED_LINE_STYLE}
                ></div>
            ))}
        </div>
    )
}

const GLOBAL_RULES_CONTENT_ID = 'manual-exam-global-rules-content'
const HISTORY_CONTENT_ID = 'manual-exam-history-content'

const DIFFICULTY_OPTIONS: Array<{ value: number; label: string }> = [
    { value: 5, label: 'Seviye 5 (0 - 0,20 arası, en kolay)' },
    { value: 4, label: 'Seviye 4 (0,20 - 0,40)' },
    { value: 3, label: 'Seviye 3 (0,40 - 0,60)' },
    { value: 2, label: 'Seviye 2 (0,60 - 0,80)' },
    { value: 1, label: 'Seviye 1 (0,80 - 1 arası, en zor)' },
]

type QuestionMetadata = {
    difficultyLabel?: string | null
    difficultyValue?: number | null
    learningObjectiveName?: string | null
}

type AutoSelectionPreparedQuestion = {
    questionBankId: number
    groupId: number
    groupName: string
    groupType: string
    learningObjectiveId?: number | null
    sharedContentId?: number | null
    optionOrder: number[]
    difficulty?: number | null
}

type AutoSelectionCandidate = {
    key: string
    sharedContentId?: number | null
    totalQuestions: number
    questions: AutoSelectionPreparedQuestion[]
    learningObjectiveIds: Set<number>
    difficulty?: number | null
}

const pickFirstString = (...values: Array<unknown>): string => {
    for (const value of values) {
        if (typeof value === 'string') {
            const trimmed = value.trim()
            if (trimmed.length > 0) {
                return trimmed
            }
        }
    }

    return ''
}

const pickFirstNumber = (...values: Array<unknown>): number | null => {
    for (const value of values) {
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value
        }
        if (typeof value === 'string') {
            const trimmed = value.trim()
            if (trimmed.length === 0) {
                continue
            }
            const normalized = trimmed.replace(',', '.')
            const numeric = Number(normalized)
            if (!Number.isNaN(numeric) && Number.isFinite(numeric)) {
                return numeric
            }
        }
    }

    return null
}

const shuffleArray = <T,>(items: T[]): T[] => {
    const cloned = [...items]
    for (let index = cloned.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(Math.random() * (index + 1))
        const temp = cloned[index]
        cloned[index] = cloned[swapIndex]
        cloned[swapIndex] = temp
    }
    return cloned
}

const normalizeDifficultyValue = (value: number | null | undefined): number | null => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
        return null
    }

    if (value >= 0 && value <= 1) {
        if (value <= 0.2) {
            return 5
        }
        if (value <= 0.4) {
            return 4
        }
        if (value <= 0.6) {
            return 3
        }
        if (value <= 0.8) {
            return 2
        }
        return 1
    }

    const rounded = Math.round(value)
    if (rounded >= 1 && rounded <= 5) {
        return rounded
    }

    return null
}

const extractAcademicTermFromSemesterCourse = (semesterCourse: any): string => {
    if (!semesterCourse) {
        return ''
    }

    const direct = pickFirstString(
        semesterCourse?.academicTerm,
        semesterCourse?.AcademicTerm,
        semesterCourse?.semesterTerm,
        semesterCourse?.SemesterTerm,
        semesterCourse?.semesterName,
        semesterCourse?.SemesterName,
        semesterCourse?.semester,
        semesterCourse?.Semester,
        semesterCourse?.termName,
        semesterCourse?.TermName,
        semesterCourse?.periodName,
        semesterCourse?.PeriodName
    )

    if (direct) {
        return direct
    }

    const semester = semesterCourse?.Semester || semesterCourse?.semester
    if (semester) {
        const named = pickFirstString(semester?.name, semester?.Name)
        if (named) {
            return named
        }

        const yearValue = semester?.year ?? semester?.Year
        const periodValue = semester?.period ?? semester?.Period
        const yearText =
            typeof yearValue === 'number' || typeof yearValue === 'string'
                ? String(yearValue).trim()
                : ''
        const periodText =
            typeof periodValue === 'number' || typeof periodValue === 'string'
                ? String(periodValue).trim()
                : ''

        if (yearText && periodText) {
            return `${yearText} / ${periodText}`
        }

        if (yearText) {
            return yearText
        }

        if (periodText) {
            return periodText
        }
    }

    return ''
}

type ManualExamTypeOption = { id: number; name: string }
type ManualExamTypeGroup = {
    key: string
    baseName: string
    options: Array<{ id: number; fullName: string; order: number | null; orderLabel: string }>
}

const EXAM_TYPE_ORDER_REGEX = /^(\d+)\.\s*(.+)$/

const buildExamTypeGroups = (items: ManualExamTypeOption[]): ManualExamTypeGroup[] => {
    const groups = new Map<string, ManualExamTypeGroup>()

    items.forEach((item, index) => {
        const rawName = (item?.name || '').trim()
        const matched = rawName.match(EXAM_TYPE_ORDER_REGEX)
        const order = matched ? Number(matched[1]) : null
        const baseName = (matched ? matched[2] : rawName).trim() || rawName || `Sınav Türü ${index + 1}`
        const key = baseName.toLocaleLowerCase('tr-TR')
        const displayOrder = matched ? Number(matched[1]) : 1
        const orderLabel = `${displayOrder}. ${baseName}`

        if (!groups.has(key)) {
            groups.set(key, {
                key,
                baseName,
                options: [],
            })
        }

        groups.get(key)?.options.push({
            id: item.id,
            fullName: rawName || baseName,
            order,
            orderLabel,
        })
    })

    return Array.from(groups.values()).map((group) => ({
        ...group,
        options: [...group.options].sort((a, b) => {
            const aOrder = a.order ?? 1
            const bOrder = b.order ?? 1
            if (aOrder !== bOrder) {
                return aOrder - bOrder
            }
            if (a.order === null && b.order !== null) {
                return -1
            }
            if (a.order !== null && b.order === null) {
                return 1
            }
            return a.fullName.localeCompare(b.fullName, 'tr')
        }),
    }))
}

const getOrderedAssignmentOptions = (
    question: ExamQuestionAssignment
): ExamQuestionAssignmentOption[] => {
    if (!question || !Array.isArray(question.options)) {
        return []
    }

    const baseOrder = Array.isArray(question.optionOrder) && question.optionOrder.length > 0
        ? question.optionOrder
        : question.options.map((option) => option.id)

    return baseOrder
        .map((id) => question.options.find((option) => option.id === id))
        .filter((option): option is ExamQuestionAssignmentOption => Boolean(option))
}

const buildManualExamHeaderTitle = (
    courseName?: string | null,
    examName?: string | null,
    academicTerm?: string | null
): string => {
    const trimmedTerm = typeof academicTerm === 'string' ? academicTerm.trim() : ''
    const trimmedCourse = typeof courseName === 'string' ? courseName.trim() : ''
    const trimmedExam = typeof examName === 'string' ? examName.trim() : ''

    const parts: string[] = []

    if (trimmedTerm) {
        parts.push(trimmedTerm)
    }

    if (trimmedCourse) {
        parts.push(`Kurul: ${trimmedCourse}`)
    }

    if (trimmedExam) {
        parts.push(`Sınav: ${trimmedExam}`)
    }

    if (parts.length > 0) {
        return parts.join(' | ')
    }

    return 'Sınav Oluşturma'
}

const ManualExamBuilder: React.FC = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const locationState = (location.state as any) || {}

    const initialExamId = locationState?.examId ? Number(locationState.examId) : null
    const initialSemesterCourseId = locationState?.semesterCourseId
        ? Number(locationState.semesterCourseId)
        : locationState?.id
            ? Number(locationState.id)
            : null
    const initialAcademicTerm =
        pickFirstString(
            locationState?.academicTerm,
            locationState?.semesterName,
            locationState?.semesterTerm,
            locationState?.termName
        ) || extractAcademicTermFromSemesterCourse(locationState?.semesterCourse || locationState?.SemesterCourse)

    const [examId, setExamId] = useState<number | null>(initialExamId)
    const [semesterCourseId, setSemesterCourseId] = useState<number | null>(initialSemesterCourseId)
    const initialCourseName =
        (typeof locationState?.boardModuleName === 'string' && locationState.boardModuleName) ||
        (typeof locationState?.courseName === 'string' && locationState.courseName) ||
        (typeof locationState?.name === 'string' && locationState.name) ||
        ''
    const [courseName, setCourseName] = useState<string>(initialCourseName)
    const [examTypes, setExamTypes] = useState<ManualExamTypeOption[]>([])
    const [selectedExamType, setSelectedExamType] = useState<number | null>(null)
    const [instructions, setInstructions] = useState('')
    const [contribution, setContribution] = useState<number>(100)
    const [plannedQuestionCount, setPlannedQuestionCount] = useState<number>(0)
    const [examName, setExamName] = useState<string>('')
    const [isExamNameManuallyEdited, setIsExamNameManuallyEdited] = useState<boolean>(false)
    const [isMedicalFaculty, setIsMedicalFaculty] = useState<boolean>(false)
    const [examDuration, setExamDuration] = useState<string>('')
    const [isExamDeliveryMethodEnabled, setIsExamDeliveryMethodEnabled] = useState<boolean>(
        Boolean(locationState?.isExamDeliveryMethodEnabled ?? locationState?.IsExamDeliveryMethodEnabled)
    )
    const [selectedExamDeliveryMethod, setSelectedExamDeliveryMethod] = useState<ExamDeliveryMethod>('FaceToFace')
    const [globalExamRules, setGlobalExamRules] = useState<string>('')
    const [tenantGlobalExamRules, setTenantGlobalExamRules] = useState<string>('')
    const [isGlobalRulesExpanded, setIsGlobalRulesExpanded] = useState<boolean>(false)
    const [academicTerm, setAcademicTerm] = useState<string>(initialAcademicTerm || '')
    const [isBoardCourse, setIsBoardCourse] = useState<boolean>(
        typeof locationState?.isBoard === 'boolean' ? Boolean(locationState.isBoard) : false
    )
    const [departments, setDepartments] = useState<Array<{ id: number; name?: string }>>([])
    const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null)
    const [questionPool, setQuestionPool] = useState<ExamQuestionPoolResponse | null>(null)
    const [questionPoolIncludesUsedQuestions, setQuestionPoolIncludesUsedQuestions] = useState<boolean>(false)
    const [courseShareInfo, setCourseShareInfo] = useState<ManualExamCourseShareInfo | null>(null)
    const [selectedShareCourseIds, setSelectedShareCourseIds] = useState<number[]>(() =>
        typeof initialSemesterCourseId === 'number' ? [initialSemesterCourseId] : []
    )
    const [loadingCourseShareInfo, setLoadingCourseShareInfo] = useState<boolean>(false)
    const [questionMetadataMap, setQuestionMetadataMap] = useState<Record<number, QuestionMetadata>>({})
    const [isInitialLoading, setIsInitialLoading] = useState<boolean>(Boolean(initialExamId))
    const [assignments, setAssignments] = useState<ExamQuestionAssignmentsResponse | null>(null)
    const [groupState, setGroupState] = useState<ExamQuestionAssignmentGroup[]>([])
    const [pointInputs, setPointInputs] = useState<Record<number, string>>({})
    const [autoCalculatePoints, setAutoCalculatePoints] = useState<boolean>(() => {
        if (typeof window === 'undefined') {
            return true
        }
        const storedValue = window.localStorage.getItem(AUTO_CALCULATE_POINTS_STORAGE_KEY)
        if (storedValue === 'false') {
            return false
        }
        if (storedValue === 'true') {
            return true
        }
        return true
    })
    const [selectedQuestion, setSelectedQuestion] = useState<any>(null)
    const [isCompactViewport, setIsCompactViewport] = useState<boolean>(() =>
        typeof window !== 'undefined' ? window.innerWidth < 992 : false
    )
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState<boolean>(false)
    const [loading, setLoading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [pointSaving, setPointSaving] = useState(false)
    const [bookletCodes, setBookletCodes] = useState<string[]>([DEFAULT_BOOKLET_CODES[0]])
    const [shuffleSharedContentQuestions, setShuffleSharedContentQuestions] = useState<boolean>(true)
    const [sharedContentShuffleStates, setSharedContentShuffleStates] = useState<Record<number, boolean>>({})
    const [lastGeneratedAt, setLastGeneratedAt] = useState<string | null>(null)
    const [bookletLoading, setBookletLoading] = useState(false)
    const [downloading, setDownloading] = useState(false)
    const [fixedBookletCount, setFixedBookletCount] = useState<number | null>(null)
    const [answerKeys, setAnswerKeys] = useState<Record<string, ManualExamAnswerKeyItem[]>>({})
    const [answerKeyGeneratedAt, setAnswerKeyGeneratedAt] = useState<string | null>(null)
    const [hasExistingBooklets, setHasExistingBooklets] = useState(false)
    const [hasBookletAnswers, setHasBookletAnswers] = useState(false)
    const [questionSpacingLevel, setQuestionSpacingLevel] = useState<number>(1)
    const [downloadFormat, setDownloadFormat] = useState<'booklet' | 'sheet'>('booklet')
    const [layoutSettings, setLayoutSettings] = useState<ManualExamBookletLayoutSettings>(
        createDefaultManualExamLayoutSettings()
    )
    const [defaultLayoutSettings, setDefaultLayoutSettings] = useState<ManualExamBookletLayoutSettings | null>(null)
    const [isDynamicLayoutEnabled, setIsDynamicLayoutEnabled] = useState(false)
    const [examStatus, setExamStatus] = useState<ManualExamStatus>('Preparing')
    const [isMeasurementCenterActive, setIsMeasurementCenterActive] = useState<boolean>(false)
    const [canManageStatus, setCanManageStatus] = useState<boolean>(false)
    const [canSendToMeasurementCenter, setCanSendToMeasurementCenter] = useState<boolean>(false)
    const [statusUpdating, setStatusUpdating] = useState<boolean>(false)
    const [canManageBooklets, setCanManageBooklets] = useState<boolean>(true)
    const [measurementCenterSubmissionNote, setMeasurementCenterSubmissionNote] = useState<string | null>(null)
    const [measurementCenterReturnNote, setMeasurementCenterReturnNote] = useState<string | null>(null)
    const [measurementCenterReturnedAt, setMeasurementCenterReturnedAt] = useState<string | null>(null)
    const [measurementCenterReturnedByName, setMeasurementCenterReturnedByName] = useState<string | null>(null)
    const [measurementCenterHistory, setMeasurementCenterHistory] = useState<
        ManualExamMeasurementCenterHistoryItem[]
    >([])
    const [supplementaryMaterialPath, setSupplementaryMaterialPath] = useState<string | null>(null)
    const [supplementaryMaterialFileName, setSupplementaryMaterialFileName] = useState<string | null>(null)
    const [selectedMaterialFile, setSelectedMaterialFile] = useState<File | null>(null)
    const [materialInputKey, setMaterialInputKey] = useState<string>('manual-material-input')
    const [materialUploading, setMaterialUploading] = useState<boolean>(false)
    const [materialDeleting, setMaterialDeleting] = useState<boolean>(false)
    const [isHistoryExpanded, setIsHistoryExpanded] = useState<boolean>(false)
    const [isSendToMeasurementCenterModalOpen, setIsSendToMeasurementCenterModalOpen] = useState(false)
    const [sendToMeasurementCenterNote, setSendToMeasurementCenterNote] = useState('')
    const [showPreviouslyUsedQuestions, setShowPreviouslyUsedQuestions] = useState<boolean>(false)
    const [availableTags, setAvailableTags] = useState<QuestionTagSummary[]>([])
    const [isTagListLoading, setIsTagListLoading] = useState<boolean>(false)
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])
    const [isAutoAddModalOpen, setIsAutoAddModalOpen] = useState<boolean>(false)
    const [autoAddTab, setAutoAddTab] = useState<'tag' | 'outcome'>('tag')
    const [autoAddSelectedTagId, setAutoAddSelectedTagId] = useState<number | null>(null)
    const [autoAddTagCount, setAutoAddTagCount] = useState<string>('1')
    const [autoAddIncludeUsed, setAutoAddIncludeUsed] = useState<boolean>(false)
    const [autoAddOutcomeIncludeUsed, setAutoAddOutcomeIncludeUsed] = useState<boolean>(false)
    const [autoAddOutcomeRows, setAutoAddOutcomeRows] = useState<Array<{ objectiveId: number | null; count: string }>>([
        { objectiveId: null, count: '' },
    ])
    const [autoAddDifficultyRows, setAutoAddDifficultyRows] = useState<Array<{ difficulty: number | null; count: string }>>([
        { difficulty: null, count: '' },
    ])
    const [autoAddOutcomePool, setAutoAddOutcomePool] = useState<ExamQuestionPoolResponse | null>(null)
    const [autoAddLoading, setAutoAddLoading] = useState<boolean>(false)
    const [autoAddSubmitting, setAutoAddSubmitting] = useState<boolean>(false)
    const examNameRequestIdRef = useRef(0)
    const restrictionMessageShownRef = useRef<string | null>(null)

    const existingQuestionIds = useMemo(() => {
        if (!groupState.length) {
            return new Set<number>()
        }
        const ids = groupState.flatMap((group) => group.questions.map((q) => q.questionBankId))
        return new Set(ids)
    }, [groupState])

    const groupedExamTypes = useMemo(() => buildExamTypeGroups(examTypes), [examTypes])
    const selectedExamTypeGroup = useMemo(
        () => groupedExamTypes.find((group) => group.options.some((option) => option.id === selectedExamType)) || null,
        [groupedExamTypes, selectedExamType]
    )
    const selectedExamTypeBaseKey = selectedExamTypeGroup?.key || ''

    const apiBaseUrl = useMemo(() => {
        const apiUrl = process.env.REACT_APP_API_URL || ''
        if (apiUrl.endsWith('/api/')) {
            return apiUrl.slice(0, -4)
        }
        if (apiUrl.endsWith('/api')) {
            return apiUrl.slice(0, -3)
        }
        return apiUrl
    }, [])

    const extractMaterialPath = useCallback((input: any): string | null => {
        const candidate =
            (typeof input?.path === 'string' && input.path) ||
            (typeof input?.materialPath === 'string' && input.materialPath) ||
            (typeof input?.supplementaryMaterialPath === 'string' && input.supplementaryMaterialPath) ||
            (typeof input?.SupplementaryMaterialPath === 'string' && input.SupplementaryMaterialPath)
        if (typeof candidate === 'string' && candidate.trim().length > 0) {
            return candidate.trim()
        }
        return null
    }, [])

    const extractMaterialFileName = useCallback((input: any, fallback?: string | null): string | null => {
        const candidate =
            (typeof input?.fileName === 'string' && input.fileName) ||
            (typeof input?.materialFileName === 'string' && input.materialFileName) ||
            (typeof input?.supplementaryMaterialFileName === 'string' && input.supplementaryMaterialFileName) ||
            (typeof input?.SupplementaryMaterialFileName === 'string' && input.SupplementaryMaterialFileName)
        if (typeof candidate === 'string' && candidate.trim().length > 0) {
            return candidate.trim()
        }
        return fallback ?? null
    }, [])

    const materialDownloadUrl = useMemo(() => {
        if (!supplementaryMaterialPath) {
            return ''
        }
        if (supplementaryMaterialPath.startsWith('http')) {
            return supplementaryMaterialPath
        }
        return `${apiBaseUrl}${supplementaryMaterialPath}`
    }, [apiBaseUrl, supplementaryMaterialPath])

    const normalizeSharedContentShuffleStates = useCallback(
        (input?: Record<number, boolean> | null) => {
            const normalized: Record<number, boolean> = {}
            if (!input) {
                return normalized
            }
            Object.entries(input).forEach(([rawKey, rawValue]) => {
                const key = Number(rawKey)
                if (!Number.isFinite(key) || key <= 0) {
                    return
                }
                if (typeof rawValue !== 'boolean') {
                    return
                }
                normalized[key] = rawValue
            })
            return normalized
        },
        []
    )

    const buildSharedContentShufflePayload = useCallback(
        (overrides?: Record<number, boolean> | null) => {
            const source = overrides ?? sharedContentShuffleStates ?? {}
            return normalizeSharedContentShuffleStates(source)
        },
        [normalizeSharedContentShuffleStates, sharedContentShuffleStates]
    )

    const getSharedContentShuffleValue = useCallback(
        (sharedContentId?: number | null) => {
            if (!Number.isFinite(sharedContentId) || !sharedContentId) {
                return shuffleSharedContentQuestions
            }

            const targetId = Number(sharedContentId)
            if (typeof sharedContentShuffleStates[targetId] === 'boolean') {
                return sharedContentShuffleStates[targetId]
            }

            return shuffleSharedContentQuestions
        },
        [sharedContentShuffleStates, shuffleSharedContentQuestions]
    )

    const shareCourseSkeletonItems = useMemo(() => Array.from({ length: 3 }, (_, index) => index), [])

    const canAdjustLayoutSettings = useMemo(
        () => isDynamicLayoutEnabled && canManageBooklets,
        [isDynamicLayoutEnabled, canManageBooklets]
    )

    const buildLayoutPayload = useCallback(() => {
        const hasCustomLogo = Boolean(layoutSettings?.cover?.logoBase64)
        const shouldSendLayout =
            isDynamicLayoutEnabled ||
            hasCustomLogo ||
            layoutSettings?.insertBlankPageAfterCover ||
            layoutSettings?.printFrontCover === false

        if (!shouldSendLayout) {
            return undefined
        }

        return normalizeManualExamLayoutSettings(layoutSettings)
    }, [isDynamicLayoutEnabled, layoutSettings])

    const buildAutoSelectionCandidates = useCallback(
        (pool: ExamQuestionPoolResponse | null, includeUsedQuestions: boolean): AutoSelectionCandidate[] => {
            if (!pool || !Array.isArray(pool.learningObjectives)) {
                return []
            }

            const candidateMap = new Map<string, AutoSelectionCandidate>()

            pool.learningObjectives.forEach((objective) => {
                const objectiveId = typeof objective?.id === 'number' ? objective.id : null
                const objectiveLabel = pickFirstString(
                    objective?.name,
                    objective?.learningObjectiveName,
                    objective?.boardModuleName,
                    objective?.departmentName
                )

                objective.questions.forEach((question) => {
                    const questionBankId = Number(question.questionBankId)
                    if (!Number.isFinite(questionBankId)) {
                        return
                    }

                    if (existingQuestionIds.has(questionBankId)) {
                        return
                    }

                    const usageValue = Number.isFinite(question.usageCount)
                        ? Number(question.usageCount)
                        : Number(question.usageCount || 0)

                    if (!includeUsedQuestions && usageValue > 0) {
                        return
                    }

                    const learningObjectiveId = Number.isFinite(question.learningObjectiveId)
                        ? Number(question.learningObjectiveId)
                        : objectiveId

                    const resolvedGroupId = isBoardCourse
                        ? (Number.isFinite(question.departmentId)
                            ? Number(question.departmentId)
                            : learningObjectiveId ?? selectedDepartmentId ?? null)
                        : learningObjectiveId ?? selectedDepartmentId ?? null

                    if (!resolvedGroupId || !Number.isFinite(resolvedGroupId)) {
                        return
                    }

                    const learningObjectiveName = pickFirstString(
                        question.learningObjectiveName,
                        objectiveLabel
                    )

                    const fallbackGroupName = isBoardCourse ? 'Birim' : 'Öğrenme Çıktısı'
                    const groupName = pickFirstString(
                        isBoardCourse ? question.departmentName : learningObjectiveName,
                        isBoardCourse ? objective?.departmentName : objectiveLabel,
                        isBoardCourse ? objective?.boardModuleName : learningObjectiveName,
                        fallbackGroupName
                    )

                    const optionOrder = (question.questionItems || [])
                        .slice()
                        .sort((a, b) => (a.order || 0) - (b.order || 0) || a.id - b.id)
                        .map((item) => item.id)

                    const rawDifficultyValue = pickFirstNumber(
                        (question as any)?.difficulty,
                        (question as any)?.Difficulty
                    )
                    const difficultyValue = normalizeDifficultyValue(rawDifficultyValue)

                    const key = question.sharedContentId
                        ? `shared-${question.sharedContentId}`
                        : `single-${questionBankId}`

                    if (!candidateMap.has(key)) {
                        candidateMap.set(key, {
                            key,
                            sharedContentId: question.sharedContentId,
                            totalQuestions: 0,
                            questions: [],
                            learningObjectiveIds: new Set<number>(),
                            difficulty: typeof difficultyValue === 'number' ? difficultyValue : undefined,
                        })
                    }

                    const candidate = candidateMap.get(key)!

                    if (typeof difficultyValue === 'number') {
                        if (typeof candidate.difficulty === 'number' && candidate.difficulty !== difficultyValue) {
                            candidate.difficulty = null
                        } else if (typeof candidate.difficulty !== 'number') {
                            candidate.difficulty = difficultyValue
                        }
                    }

                    candidate.questions.push({
                        questionBankId,
                        groupId: Number(resolvedGroupId),
                        groupName: groupName || fallbackGroupName,
                        groupType: isBoardCourse ? 'department' : 'outcome',
                        learningObjectiveId: learningObjectiveId ?? undefined,
                        sharedContentId: question.sharedContentId ?? undefined,
                        optionOrder,
                        difficulty: typeof difficultyValue === 'number' ? difficultyValue : null,
                    })
                    if (typeof learningObjectiveId === 'number') {
                        candidate.learningObjectiveIds.add(learningObjectiveId)
                    }
                    candidate.totalQuestions += 1
                })
            })

            return Array.from(candidateMap.values()).filter((candidate) => candidate.totalQuestions > 0)
        },
        [existingQuestionIds, isBoardCourse, selectedDepartmentId]
    )

    const normalizedTagOptions = useMemo(() => {
        return availableTags
            .filter((tag) => tag && typeof tag.id === 'number')
            .map((tag) => {
                const rawName = typeof tag.name === 'string' ? tag.name.trim() : ''
                return {
                    id: Number(tag.id),
                    name: rawName,
                    questionCount:
                        typeof tag.questionCount === 'number' && Number.isFinite(tag.questionCount)
                            ? tag.questionCount
                            : null,
                }
            })
            .filter((tag) => tag.name.length > 0)
            .sort((a, b) => a.name.localeCompare(b.name, 'tr-TR'))
    }, [availableTags])

    const selectedTagIdSet = useMemo(() => new Set(selectedTagIds), [selectedTagIds])

    const autoAddOutcomeOptions = useMemo(() => {
        const candidates = buildAutoSelectionCandidates(autoAddOutcomePool, autoAddOutcomeIncludeUsed)
        const availableCounts = new Map<number, number>()
        candidates.forEach((candidate) => {
            candidate.questions.forEach((question) => {
                const objectiveId = typeof question.learningObjectiveId === 'number' ? question.learningObjectiveId : null
                if (objectiveId !== null) {
                    availableCounts.set(objectiveId, (availableCounts.get(objectiveId) || 0) + 1)
                }
            })
        })

        const nameLookup = new Map<number, string>()
        autoAddOutcomePool?.learningObjectives?.forEach((objective) => {
            const id = Number(objective?.id)
            if (!Number.isFinite(id)) {
                return
            }
            const label = pickFirstString(
                objective?.name,
                objective?.learningObjectiveName,
                objective?.boardModuleName,
                objective?.departmentName
            )
            if (label) {
                nameLookup.set(id, label)
            }
        })

        const options = Array.from(availableCounts.entries()).map(([id, count]) => ({
            id,
            name: nameLookup.get(id) || 'Öğrenme Çıktısı',
            count,
        }))

        return options.sort((a, b) => a.name.localeCompare(b.name, 'tr-TR'))
    }, [autoAddOutcomeIncludeUsed, autoAddOutcomePool, buildAutoSelectionCandidates])

    const normalizedDifficultyRequests = useMemo(() => {
        const rows = autoAddDifficultyRows
            .map((row) => ({
                difficulty: Number.isFinite(row.difficulty) ? Number(row.difficulty) : null,
                count: Math.max(0, Math.floor(Number(row.count) || 0)),
            }))
            .filter((row) => row.difficulty && row.count > 0) as Array<{ difficulty: number; count: number }>

        const aggregated = rows.reduce<Record<number, number>>((acc, row) => {
            if (!acc[row.difficulty]) {
                acc[row.difficulty] = 0
            }
            acc[row.difficulty] += row.count
            return acc
        }, {})

        return aggregated
    }, [autoAddDifficultyRows])

    const handleToggleTagFilter = useCallback((tagId: number) => {
        if (!Number.isFinite(tagId)) {
            return
        }
        setSelectedTagIds((prev) => {
            const normalizedId = Number(tagId)
            if (prev.includes(normalizedId)) {
                return prev.filter((id) => id !== normalizedId)
            }
            return [...prev, normalizedId]
        })
    }, [])

    const handleClearTagFilters = useCallback(() => {
        setSelectedTagIds([])
    }, [])

    const downloadBaseName = useMemo(() => buildManualExamFileBaseName(courseName, examName), [courseName, examName])
    const displayedGlobalExamRules = useMemo(() => {
        if (typeof globalExamRules === 'string' && globalExamRules.trim().length > 0) {
            return globalExamRules
        }

        if (typeof tenantGlobalExamRules === 'string' && tenantGlobalExamRules.trim().length > 0) {
            return tenantGlobalExamRules
        }

        return ''
    }, [globalExamRules, tenantGlobalExamRules])
    const canEditExamName = useMemo(() => isMedicalFaculty, [isMedicalFaculty])
    const downloadButtonLabel = useMemo(
        () => (downloadFormat === 'sheet' ? 'Soru Kağıdını İndir' : 'PDF Paketini İndir'),
        [downloadFormat]
    )
    const isBookletCountFixed = useMemo(
        () => typeof fixedBookletCount === 'number' && Number.isFinite(fixedBookletCount),
        [fixedBookletCount]
    )
    const displayedBookletCount = useMemo(
        () => (isBookletCountFixed && fixedBookletCount ? fixedBookletCount : bookletCodes.length),
        [bookletCodes, fixedBookletCount, isBookletCountFixed]
    )
    const applyIncomingBookletCodes = useCallback(
        (codes: string[]) => {
            const sanitized = sanitizeBookletCodes(Array.isArray(codes) ? codes : [])
            if (typeof fixedBookletCount === 'number' && Number.isFinite(fixedBookletCount)) {
                setBookletCodes(ensureBookletCount(sanitized, fixedBookletCount))
                return
            }
            setBookletCodes(sanitized)
        },
        [fixedBookletCount]
    )
    const statusLabel = useMemo(() => MANUAL_EXAM_STATUS_LABELS[examStatus] ?? 'Bilinmiyor', [examStatus])
    const statusBadgeClass = useMemo(
        () => MANUAL_EXAM_STATUS_BADGES[examStatus] ?? 'badge badge-light-secondary',
        [examStatus]
    )
    const isPreparingStatus = examStatus === 'Preparing'
    const canEditExamContent = useMemo(
        () => isPreparingStatus && canManageBooklets,
        [isPreparingStatus, canManageBooklets]
    )
    const canAdjustBookletSettings = useMemo(
        () => canManageBooklets && (isPreparingStatus || canManageStatus),
        [canManageBooklets, canManageStatus, isPreparingStatus]
    )

    const renderedReturnNote = useMemo(() => {
        if (!measurementCenterReturnNote) {
            return null
        }
        const parts = measurementCenterReturnNote.split(/\r?\n/)
        return parts.map((part, index) => (
            <span key={`measurement-center-note-${index}`}>
                {part}
                {index < parts.length - 1 && <br />}
            </span>
        ))
    }, [measurementCenterReturnNote])

    const renderedSubmissionNote = useMemo(() => {
        if (!measurementCenterSubmissionNote) {
            return null
        }
        const parts = measurementCenterSubmissionNote.split(/\r?\n/)
        return parts.map((part, index) => (
            <span key={`measurement-center-submission-note-${index}`}>
                {part}
                {index < parts.length - 1 && <br />}
            </span>
        ))
    }, [measurementCenterSubmissionNote])

    const formattedReturnDate = useMemo(() => {
        if (!measurementCenterReturnedAt) {
            return null
        }
        const parsed = new Date(measurementCenterReturnedAt)
        if (Number.isNaN(parsed.getTime())) {
            return measurementCenterReturnedAt
        }
        return parsed.toLocaleString()
    }, [measurementCenterReturnedAt])

    const formatHistoryDateTime = useCallback((value?: string | null) => {
        if (!value) {
            return '-'
        }
        const parsed = new Date(value)
        if (Number.isNaN(parsed.getTime())) {
            return value
        }
        return parsed.toLocaleString()
    }, [])

    const sortedMeasurementCenterHistory = useMemo(() => {
        if (!measurementCenterHistory || measurementCenterHistory.length === 0) {
            return [] as ManualExamMeasurementCenterHistoryItem[]
        }

        const cloned = [...measurementCenterHistory]
        cloned.sort((a, b) => {
            const aTime = a?.returnedAt ? new Date(a.returnedAt).getTime() : 0
            const bTime = b?.returnedAt ? new Date(b.returnedAt).getTime() : 0
            return bTime - aTime
        })

        return cloned
    }, [measurementCenterHistory])

    const headerTitle = useMemo(
        () => buildManualExamHeaderTitle(courseName, examName, academicTerm),
        [courseName, examName, academicTerm]
    )

    const showInitialLoader = isInitialLoading || (loading && !questionPool)

    const { invalidPointIds, totalPoint } = useMemo(() => {
        const invalid = new Set<number>()
        let sum = 0
        groupState.forEach((group) => {
            group.questions.forEach((question) => {
                const rawValue = pointInputs[question.examQuestionId]
                if (typeof rawValue !== 'string' || rawValue.trim() === '') {
                    invalid.add(question.examQuestionId)
                    return
                }
                const normalized = rawValue.replace(',', '.').trim()
                const numeric = Number.parseFloat(normalized)
                if (Number.isNaN(numeric) || !Number.isFinite(numeric) || numeric < 0) {
                    invalid.add(question.examQuestionId)
                    return
                }
                sum += numeric
            })
        })
        return {
            invalidPointIds: invalid,
            totalPoint: Math.round(sum * 100) / 100,
        }
    }, [groupState, pointInputs])

    const isTotalValid = Math.abs(totalPoint - 100) <= 0.01

    const questionPoolQuestionCount = useMemo(() => {
        const totalFromResponse = questionPool?.totalQuestionCount
        if (typeof totalFromResponse === 'number' && Number.isFinite(totalFromResponse)) {
            return Math.max(0, Math.floor(totalFromResponse))
        }

        if (!questionPool) {
            return 0
        }

        return questionPool.learningObjectives.reduce((sum, objective) => sum + objective.questions.length, 0)
    }, [questionPool])

    const hasPreviouslyUsedQuestions = useMemo(() => {
        if (typeof questionPool?.hasPreviouslyUsedQuestions === 'boolean') {
            return questionPool.hasPreviouslyUsedQuestions
        }

        if (!questionPool) {
            return false
        }

        return questionPool.learningObjectives.some((objective) =>
            objective.questions.some((question) => (question.usageCount ?? 0) > 0)
        )
    }, [questionPool])

    const enhanceHtmlContent = useCallback((content?: string | null) => wrapManualExamImages(content || ''), [])

    const buildQuestionHtml = useCallback(
        (question: any) => enhanceHtmlContent(getImageSafeQuestionName(question)),
        [enhanceHtmlContent]
    )

    const buildOptionHtml = useCallback((option: any) => enhanceHtmlContent(getImageSafeOptionName(option)), [enhanceHtmlContent])

    const findDepartmentName = useCallback(
        (id: number | null | undefined) => {
            if (id === null || id === undefined) {
                return undefined
            }
            const target = departments.find((d) => d.id === id)
            if (!target) {
                return undefined
            }
            if (target.name && target.name.trim().length > 0) {
                return target.name
            }
            return isBoardCourse ? 'İsimsiz Birim' : 'İsimsiz Çıktı'
        },
        [departments, isBoardCourse]
    )

    const loadExamTypes = useCallback(() => {
        getExamTypes()
            .then((items) => {
                setExamTypes(items || [])
                if (!selectedExamType && items && items.length > 0) {
                    setSelectedExamType(items[0].id)
                }
            })
            .catch(() => {
                setExamTypes([])
            })
    }, [selectedExamType])

    const loadManualSettings = useCallback(
        async (targetExamId: number) => {
            try {
                const data = await getManualExamSettings(targetExamId)
                if (data) {
                    const incoming =
                        data.bookletCodes && data.bookletCodes.length
                            ? data.bookletCodes
                            : [DEFAULT_BOOKLET_CODES[0]]
                    applyIncomingBookletCodes(incoming)
                    if (typeof data.shuffleSharedContentQuestions === 'boolean') {
                        setShuffleSharedContentQuestions(data.shuffleSharedContentQuestions)
                    } else {
                        setShuffleSharedContentQuestions(true)
                    }
                    setSharedContentShuffleStates(
                        normalizeSharedContentShuffleStates(data.sharedContentShuffleStates)
                    )
                    setLastGeneratedAt(data.lastGeneratedAt || null)
                    setHasExistingBooklets(Boolean(data.hasExistingBooklets))
                    setHasBookletAnswers(Boolean(data.hasBookletAnswers))
                    if (typeof data.examDurationInMinutes === 'number') {
                        setExamDuration(String(data.examDurationInMinutes))
                    } else {
                        setExamDuration('')
                    }

                    if (typeof data.globalExamRules === 'string' && data.globalExamRules.trim().length > 0) {
                        setGlobalExamRules(data.globalExamRules)
                        setIsGlobalRulesExpanded(false)
                    } else {
                        setGlobalExamRules('')
                        setIsGlobalRulesExpanded(false)
                    }

                    if (typeof data.status === 'string') {
                        setExamStatus(data.status as ManualExamStatus)
                    } else {
                        setExamStatus('Preparing')
                    }

                    setIsMeasurementCenterActive(Boolean(data.isMeasurementCenterActive))
                    setCanManageStatus(Boolean(data.canManageStatus))
                    setCanSendToMeasurementCenter(Boolean(data.canSendToMeasurementCenter))
                    if (typeof data.canManageBooklets === 'boolean') {
                        setCanManageBooklets(data.canManageBooklets)
                    } else {
                        setCanManageBooklets(true)
                    }
                    if (typeof data.measurementCenterReturnNote === 'string' && data.measurementCenterReturnNote.trim().length > 0) {
                        setMeasurementCenterReturnNote(data.measurementCenterReturnNote)
                    } else {
                        setMeasurementCenterReturnNote(null)
                    }
                    if (
                        typeof data.measurementCenterSubmissionNote === 'string' &&
                        data.measurementCenterSubmissionNote.trim().length > 0
                    ) {
                        setMeasurementCenterSubmissionNote(data.measurementCenterSubmissionNote)
                    } else {
                        setMeasurementCenterSubmissionNote(null)
                    }
                    setMeasurementCenterReturnedAt(data.measurementCenterReturnedAt ?? null)
                    setMeasurementCenterReturnedByName(data.measurementCenterReturnedByName ?? null)
                    if (
                        Array.isArray(data.measurementCenterReturnHistory) &&
                        data.measurementCenterReturnHistory.length > 0
                    ) {
                        setMeasurementCenterHistory(data.measurementCenterReturnHistory)
                    } else {
                        setMeasurementCenterHistory([])
                    }
                    setSupplementaryMaterialPath(extractMaterialPath(data))
                    setSupplementaryMaterialFileName(
                        extractMaterialFileName(data, supplementaryMaterialFileName)
                    )
                    setLayoutSettings(normalizeManualExamLayoutSettings(data.layoutSettings))
                    setIsDynamicLayoutEnabled(Boolean(data.isDynamicLayoutEnabled))
                    if (data.defaultLayoutSettings) {
                        setDefaultLayoutSettings(
                            normalizeManualExamLayoutSettings(data.defaultLayoutSettings)
                        )
                    } else {
                        setDefaultLayoutSettings(null)
                    }

                    if (
                        typeof data.restrictionMessage === 'string' &&
                        data.restrictionMessage.trim().length > 0
                    ) {
                        const normalizedRestrictionMessage = data.restrictionMessage.trim()
                        if (restrictionMessageShownRef.current !== normalizedRestrictionMessage) {
                            restrictionMessageShownRef.current = normalizedRestrictionMessage
                            await Swal.fire('Uyarı', normalizedRestrictionMessage, 'warning')
                        }
                    } else {
                        restrictionMessageShownRef.current = null
                    }
                } else {
                    setMeasurementCenterReturnNote(null)
                    setMeasurementCenterSubmissionNote(null)
                    setMeasurementCenterReturnedAt(null)
                    setMeasurementCenterReturnedByName(null)
                    setMeasurementCenterHistory([])
                    setSupplementaryMaterialPath(null)
                    setSupplementaryMaterialFileName(null)
                    setLayoutSettings(createDefaultManualExamLayoutSettings())
                    setSharedContentShuffleStates({})
                    setIsDynamicLayoutEnabled(false)
                    setDefaultLayoutSettings(null)
                }
            } catch (err: any) {
                console.error(err)
                if (err?.response?.status === 403) {
                    const message =
                        typeof err.response?.data === 'string'
                            ? err.response.data
                            : 'Optik okuma süreci tamamlanmadan not girişi düzenlenemez.'
                    await Swal.fire('Uyarı', message, 'warning')
                    navigate('/my-course-exam/list')
                    return
                }
                setHasExistingBooklets(false)
                setHasBookletAnswers(false)
                setGlobalExamRules('')
                setIsGlobalRulesExpanded(false)
                setExamStatus('Preparing')
                setIsMeasurementCenterActive(false)
                setCanManageStatus(false)
                setCanSendToMeasurementCenter(false)
                setCanManageBooklets(false)
                setMeasurementCenterReturnNote(null)
                setMeasurementCenterSubmissionNote(null)
                setMeasurementCenterReturnedAt(null)
                setMeasurementCenterReturnedByName(null)
                setMeasurementCenterHistory([])
                setSupplementaryMaterialPath(null)
                setSupplementaryMaterialFileName(null)
                setShuffleSharedContentQuestions(true)
                setSharedContentShuffleStates({})
                setLayoutSettings(createDefaultManualExamLayoutSettings())
                setIsDynamicLayoutEnabled(false)
                setDefaultLayoutSettings(null)
            }
        },
        [applyIncomingBookletCodes, extractMaterialFileName, extractMaterialPath]
    )

    const loadAvailableTags = useCallback(async () => {
        try {
            setIsTagListLoading(true)
            const tags = await getMyQuestionTags()
            const safeTags = Array.isArray(tags) ? tags : []
            setAvailableTags(safeTags)
            setSelectedTagIds((prev) => {
                if (!prev.length) {
                    return prev
                }
                const validIds = new Set(
                    safeTags
                        .filter((tag) => tag && typeof (tag as any).id === 'number')
                        .map((tag) => Number((tag as any).id))
                )
                const filtered = prev.filter((id) => validIds.has(id))
                return filtered.length === prev.length ? prev : filtered
            })
        } catch (err) {
            console.error(err)
            setAvailableTags([])
            setSelectedTagIds([])
        } finally {
            setIsTagListLoading(false)
        }
    }, [])

    const loadTenantSettings = useCallback(async () => {
        try {
            const settings = await fetchTenantSettings()
            if (settings?.globalExamRules && settings.globalExamRules.trim().length > 0) {
                setTenantGlobalExamRules(settings.globalExamRules)
            } else {
                setTenantGlobalExamRules('')
            }
            if (
                typeof settings?.manualExamFixedBookletCount === 'number' &&
                Number.isFinite(settings.manualExamFixedBookletCount)
            ) {
                const normalized = Math.min(
                    Math.max(Math.floor(settings.manualExamFixedBookletCount), 1),
                    DEFAULT_BOOKLET_CODES.length
                )
                setFixedBookletCount(normalized)
                setBookletCodes((prev) => ensureBookletCount(prev, normalized))
            } else {
                setFixedBookletCount(null)
                setBookletCodes((prev) => sanitizeBookletCodes(prev))
            }
            if (settings?.manualExamDefaultLayout) {
                setDefaultLayoutSettings(
                    normalizeManualExamLayoutSettings(settings.manualExamDefaultLayout)
                )
            } else {
                setDefaultLayoutSettings(null)
            }
        } catch (err) {
            console.error(err)
            setTenantGlobalExamRules('')
            setFixedBookletCount(null)
            setDefaultLayoutSettings(null)
        }
    }, [])

    const updateMedicalFacultyFlag = useCallback((course?: SemesterCourseModel | null) => {
        const organizationName =
            (typeof course?.rOrganization === 'string' && course.rOrganization) ||
            (course as any)?.ROrganization ||
            (course as any)?.rOrganization?.name ||
            (course as any)?.ROrganization?.name

        const normalized = typeof organizationName === 'string' ? organizationName.toLocaleLowerCase('tr') : ''
        setIsMedicalFaculty(Boolean(normalized) && (normalized.includes('tıp') || normalized.includes('tip')))
    }, [])

    const loadSemesterCourseInfo = useCallback(
        async (targetSemesterCourseId?: number | null) => {
            if (!targetSemesterCourseId || Number.isNaN(targetSemesterCourseId)) {
                setIsMedicalFaculty(false)
                setIsExamDeliveryMethodEnabled(false)
                return
            }

            try {
                const course = await getInstructorSemesterCourseById(targetSemesterCourseId)
                updateMedicalFacultyFlag(course || null)
                setIsExamDeliveryMethodEnabled(Boolean(course?.isExamDeliveryMethodEnabled))
            } catch (error) {
                console.error(error)
                setIsMedicalFaculty(false)
                setIsExamDeliveryMethodEnabled(false)
            }
        },
        [updateMedicalFacultyFlag]
    )

    const updateBookletCodesOnServer = useCallback(
        async (
            codes: string[],
            nextShuffleSetting?: boolean,
            nextSharedContentStates?: Record<number, boolean> | null
        ) => {
            if (!examId || !canAdjustBookletSettings) {
                return
            }
            try {
                setBookletLoading(true)
                const layoutPayload = buildLayoutPayload()
                const shuffleValue =
                    typeof nextShuffleSetting === 'boolean'
                        ? nextShuffleSetting
                        : shuffleSharedContentQuestions
                const sharedContentPayload = buildSharedContentShufflePayload(nextSharedContentStates)
                await updateManualExamBooklets({
                    examId,
                    bookletCodes: codes,
                    layoutSettings: layoutPayload,
                    shuffleSharedContentQuestions: shuffleValue,
                    sharedContentShuffleStates: sharedContentPayload,
                })
                setShuffleSharedContentQuestions(shuffleValue)
                setSharedContentShuffleStates(sharedContentPayload)
                await loadManualSettings(examId)
                setAnswerKeys({})
                setAnswerKeyGeneratedAt(null)
            } catch (err: any) {
                console.error(err)
                const message = err?.response?.data || 'Kitapçık ayarları güncellenemedi.'
                await loadManualSettings(examId)
                Swal.fire('Hata', message, 'error')
            } finally {
                setBookletLoading(false)
            }
        },
        [
            examId,
            canAdjustBookletSettings,
            buildLayoutPayload,
            loadManualSettings,
            buildSharedContentShufflePayload,
            shuffleSharedContentQuestions,
        ]
    )

    const handleToggleShuffleSharedContentQuestions = useCallback(async () => {
        const nextValue = !shuffleSharedContentQuestions
        setShuffleSharedContentQuestions(nextValue)
        await updateBookletCodesOnServer(bookletCodes, nextValue)
    }, [bookletCodes, shuffleSharedContentQuestions, updateBookletCodesOnServer])

    const handleToggleSharedContentShuffle = useCallback(
        async (sharedContentId?: number | null) => {
            if (!Number.isFinite(sharedContentId) || !sharedContentId) {
                return
            }

            const targetId = Number(sharedContentId)
            const nextValue = !getSharedContentShuffleValue(targetId)
            const nextStates = {
                ...sharedContentShuffleStates,
                [targetId]: nextValue,
            }

            await updateBookletCodesOnServer(
                bookletCodes,
                shuffleSharedContentQuestions,
                nextStates
            )
        },
        [
            bookletCodes,
            getSharedContentShuffleValue,
            sharedContentShuffleStates,
            shuffleSharedContentQuestions,
            updateBookletCodesOnServer,
        ]
    )

    const handleBookletCountChange = useCallback(
        async (count: number) => {
            if (!canAdjustBookletSettings) {
                return
            }
            if (typeof fixedBookletCount === 'number' && Number.isFinite(fixedBookletCount)) {
                return
            }
            const safeCount = Math.min(Math.max(count, 1), DEFAULT_BOOKLET_CODES.length)
            const nextCodes = ensureBookletCount(bookletCodes, safeCount)
            setBookletCodes(nextCodes)
            await updateBookletCodesOnServer(nextCodes)
        },
        [bookletCodes, canAdjustBookletSettings, fixedBookletCount, updateBookletCodesOnServer]
    )

    useEffect(() => {
        setSharedContentShuffleStates((prev) => {
            const seenSharedContent = new Set<number>()

            groupState.forEach((group) => {
                const questions = Array.isArray(group?.questions) ? group.questions : []
                questions.forEach((question) => {
                    const sharedContentId = Number(question?.sharedContentId)
                    if (!Number.isFinite(sharedContentId) || sharedContentId <= 0) {
                        return
                    }
                    seenSharedContent.add(sharedContentId)
                })
            })

            const next: Record<number, boolean> = {}
            let changed = false
            Object.entries(prev).forEach(([rawKey, rawValue]) => {
                const id = Number(rawKey)
                if (seenSharedContent.has(id)) {
                    next[id] = rawValue
                } else {
                    changed = true
                }
            })

            if (!changed) {
                return prev
            }

            return next
        })
    }, [groupState])

    const handleApplyDefaultLayout = useCallback(() => {
        if (!defaultLayoutSettings) {
            return
        }
        setLayoutSettings(normalizeManualExamLayoutSettings(defaultLayoutSettings))
    }, [defaultLayoutSettings])

    const handleOpenSendToMeasurementCenterModal = useCallback(() => {
        if (!examId) {
            return
        }
        setSendToMeasurementCenterNote(measurementCenterSubmissionNote ?? '')
        setIsSendToMeasurementCenterModalOpen(true)
    }, [examId, measurementCenterSubmissionNote])

    const handleCloseSendToMeasurementCenterModal = useCallback(() => {
        if (statusUpdating) {
            return
        }
        setIsSendToMeasurementCenterModalOpen(false)
    }, [statusUpdating])

    const handleConfirmSendToMeasurementCenter = useCallback(async () => {
        if (!examId) {
            return
        }

        const trimmedNote = sendToMeasurementCenterNote.trim()

        try {
            setStatusUpdating(true)
            await updateManualExamStatus({
                examId,
                status: 'SentToMeasurementCenter',
                submissionNote: trimmedNote.length > 0 ? trimmedNote : undefined,
            })
            setExamStatus('SentToMeasurementCenter')
            setMeasurementCenterSubmissionNote(trimmedNote.length > 0 ? trimmedNote : null)
            await loadManualSettings(examId)
            Swal.fire('Başarılı', 'Sınav ölçme merkezine gönderildi.', 'success')
            setIsSendToMeasurementCenterModalOpen(false)
        } catch (err: any) {
            console.error(err)
            const message = err?.response?.data || 'İşlem tamamlanamadı.'
            Swal.fire('Hata', message, 'error')
            await loadManualSettings(examId)
        } finally {
            setStatusUpdating(false)
        }
    }, [examId, loadManualSettings, sendToMeasurementCenterNote])

    const handleDownloadPdf = useCallback(async () => {
        if (!examId) {
            return
        }
        try {
            setDownloading(true)
            const response = await downloadManualExamPdf(examId, questionSpacingLevel, downloadFormat)
            const isSheetFormat = downloadFormat === 'sheet'
            const blob = new Blob([response.data], { type: isSheetFormat ? 'application/pdf' : 'application/zip' })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            const baseName = downloadBaseName
            link.download = isSheetFormat ? `${baseName}_soru_kagidi.pdf` : `${baseName}_kitapciklar.zip`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
        } catch (err: any) {
            console.error(err)
            const message = err?.response?.data || 'Dosya indirilemedi.'
            Swal.fire('Hata', message, 'error')
        } finally {
            setDownloading(false)
        }
    }, [downloadFormat, downloadBaseName, examId, questionSpacingLevel])

    const handleShowAnswerKeys = useCallback(async () => {
        if (!examId) {
            return
        }
        try {
            setBookletLoading(true)
            const response = await getManualExamAnswerKeys(examId)
            if (response?.keys) {
                setAnswerKeys(response.keys)
                setAnswerKeyGeneratedAt(response.generatedAt || null)
            } else {
                setAnswerKeys({})
                setAnswerKeyGeneratedAt(null)
                Swal.fire('Bilgi', 'Cevap anahtarı bulunamadı.', 'info')
            }
        } catch (err: any) {
            console.error(err)
            const message = err?.response?.data || 'Cevap anahtarı alınamadı.'
            Swal.fire('Hata', message, 'error')
        } finally {
            setBookletLoading(false)
        }
    }, [examId])

    const uploadSupplementaryMaterial = useCallback(
        async (targetExamId: number, file: File, showAlert: boolean = true) => {
            if (!targetExamId || !file) {
                return
            }

            try {
                setMaterialUploading(true)
                const response = await uploadManualExamMaterial(targetExamId, file)
                const payload = (response as any)?.data?.data ?? (response as any)?.data ?? {}
                const uploadedPath = extractMaterialPath(payload)
                const uploadedFileName = extractMaterialFileName(payload, file.name)
                setSupplementaryMaterialPath(uploadedPath)
                setSupplementaryMaterialFileName(uploadedFileName)
                setSelectedMaterialFile(null)
                setMaterialInputKey(`manual-material-${Date.now()}`)
                if (showAlert) {
                    Swal.fire('Başarılı', 'Materyal yüklendi.', 'success')
                }
            } catch (err: any) {
                console.error(err)
                const message = err?.response?.data || 'Dosya yüklenirken hata oluştu.'
                Swal.fire(showAlert ? 'Hata' : 'Uyarı', message, showAlert ? 'error' : 'warning')
            } finally {
                setMaterialUploading(false)
            }
        },
        [extractMaterialFileName, extractMaterialPath]
    )

    const handleMaterialFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) {
            setSelectedMaterialFile(null)
            return
        }

        const lowerName = file.name.toLowerCase()
        if (!lowerName.endsWith('.pdf')) {
            Swal.fire('Uyarı', 'Lütfen PDF formatında bir dosya seçin.', 'warning')
            event.target.value = ''
            setSelectedMaterialFile(null)
            return
        }

        setSelectedMaterialFile(file)
    }, [])

    const handleDeleteMaterial = useCallback(async () => {
        if (!examId) {
            return
        }

        const confirmation = await Swal.fire({
            title: 'Emin misiniz?',
            text: 'Yüklenen PDF kaldırılacaktır.',
            icon: 'question',
            confirmButtonText: 'Evet, sil',
            cancelButtonText: 'Vazgeç',
            showCancelButton: true,
        })

        if (!confirmation.isConfirmed) {
            return
        }

        try {
            setMaterialDeleting(true)
            await deleteManualExamMaterial(examId)
            setSupplementaryMaterialPath(null)
            setSupplementaryMaterialFileName(null)
            setSelectedMaterialFile(null)
            setMaterialInputKey(`manual-material-${Date.now()}`)
            Swal.fire('Başarılı', 'Materyal kaldırıldı.', 'success')
        } catch (err: any) {
            console.error(err)
            const message = err?.response?.data || 'Materyal silinirken hata oluştu.'
            Swal.fire('Hata', message, 'error')
        } finally {
            setMaterialDeleting(false)
        }
    }, [examId])

    const handleUploadMaterial = useCallback(async () => {
        if (!examId) {
            Swal.fire('Uyarı', 'Materyal yüklemek için önce sınavı oluşturun.', 'warning')
            return
        }

        if (!selectedMaterialFile) {
            Swal.fire('Uyarı', 'Lütfen yüklemek için bir PDF seçin.', 'warning')
            return
        }

        await uploadSupplementaryMaterial(examId, selectedMaterialFile, true)
    }, [examId, selectedMaterialFile, uploadSupplementaryMaterial])

    const loadExamDetails = useCallback(
        async (targetExamId: number) => {
            try {
                const exam = (await getExamById(targetExamId)) as ExamModel | undefined
                if (exam) {
                    if (isReferenceExam(exam)) {
                        await showReferenceExamWarning()
                        navigate('/my-course-exam/list', {state: locationState})
                        return
                    }

                    setInstructions(exam.instructions || '')
                    setContribution(exam.contributionPercentage || 100)
                    setSelectedExamType(exam.examTypeId || null)
                    setPlannedQuestionCount(exam.questionCount || 0)
                    setSemesterCourseId(exam.semesterCourseId || null)
                    setExamName(exam.name || '')
                    setIsExamNameManuallyEdited(true)
                    setSupplementaryMaterialPath(extractMaterialPath(exam))
                    setSupplementaryMaterialFileName(extractMaterialFileName(exam, null))
                    if (typeof exam.durationInMinutes === 'number') {
                        setExamDuration(String(exam.durationInMinutes))
                    } else {
                        setExamDuration('')
                    }
                    if (exam.examDeliveryMethod) {
                        setSelectedExamDeliveryMethod(exam.examDeliveryMethod)
                    }
                    setIsExamDeliveryMethodEnabled(Boolean(exam.isExamDeliveryMethodEnabled))
                    const semesterCourse: any = (exam as any)?.SemesterCourse
                    if (semesterCourse) {
                        const boardFlag =
                            typeof semesterCourse?.isBoard === 'boolean'
                                ? semesterCourse.isBoard
                                : typeof semesterCourse?.IsBoard === 'boolean'
                                    ? semesterCourse.IsBoard
                                    : typeof semesterCourse?.Course?.courseType?.isBoard === 'boolean'
                                        ? semesterCourse.Course.courseType.isBoard
                                        : typeof semesterCourse?.Course?.CourseType?.isBoard === 'boolean'
                                            ? semesterCourse.Course.CourseType.isBoard
                                            : undefined
                        if (typeof boardFlag === 'boolean') {
                            setIsBoardCourse(boardFlag)
                        }

                        const nextCourseName =
                            semesterCourse?.name ||
                            semesterCourse?.courseName ||
                            semesterCourse?.Course?.name ||
                            semesterCourse?.Course?.Name ||
                            ''
                        if (nextCourseName) {
                            setCourseName(nextCourseName)
                        }

                        const nextTerm = extractAcademicTermFromSemesterCourse(semesterCourse)
                        if (nextTerm) {
                            setAcademicTerm(nextTerm)
                        }
                    }
                }
            } catch (err) {
                console.error(err)
            }
        },
        [applyIncomingBookletCodes]
    )

    const loadDepartments = useCallback(
        async (scId: number) => {
            try {
                const list = await getExamQuestionPoolDepartments(scId)
                setDepartments(list || [])
                if ((list || []).length > 0) {
                    setSelectedDepartmentId((list || [])[0].id)
                } else {
                    setSelectedDepartmentId(null)
                }
            } catch (err) {
                console.error(err)
                setDepartments([])
                setSelectedDepartmentId(null)
            }
        },
        []
    )

    const loadQuestionPool = useCallback(
        async (
            scId: number,
            depId: number | null,
            currentExamId?: number | null,
            includeUsedQuestions?: boolean
        ) => {
            if (!scId) {
                setQuestionPool(null)
                setQuestionPoolIncludesUsedQuestions(false)
                return
            }
            try {
                setLoading(true)
                const pool = await getExamQuestionPool(
                    scId,
                    depId === null ? undefined : depId,
                    typeof currentExamId === 'number' ? currentExamId : undefined,
                    selectedTagIds,
                    includeUsedQuestions
                )
                setQuestionPool(pool)
                setQuestionPoolIncludesUsedQuestions(Boolean(includeUsedQuestions))
                if (pool && Array.isArray(pool.learningObjectives)) {
                    const metadataUpdates: Record<number, QuestionMetadata> = {}
                    pool.learningObjectives.forEach((objective) => {
                        const objectiveLabel = pickFirstString(
                            objective.name,
                            objective.learningObjectiveName,
                            objective.boardModuleName,
                            objective.departmentName
                        )
                        objective.questions.forEach((question) => {
                            const questionBankId = Number(question.questionBankId)
                            if (!Number.isFinite(questionBankId)) {
                                return
                            }
                            const raw = question as any
                            const difficultyLabelCandidate = pickFirstString(
                                raw?.difficultyName,
                                raw?.DifficultyName,
                                raw?.difficultyLevelName,
                                raw?.DifficultyLevelName,
                                typeof raw?.difficulty === 'string' ? raw.difficulty : undefined,
                                typeof raw?.Difficulty === 'string' ? raw.Difficulty : undefined
                            )
                            const difficultyValueCandidate = pickFirstNumber(
                                raw?.difficultyLevel,
                                raw?.DifficultyLevel,
                                typeof raw?.difficulty === 'number' ? raw.difficulty : undefined,
                                typeof raw?.Difficulty === 'number' ? raw.Difficulty : undefined
                            )
                            const normalizedDifficulty = normalizeDifficultyDetails(
                                difficultyLabelCandidate,
                                difficultyValueCandidate
                            )
                            const learningObjectiveLabel = pickFirstString(
                                question.learningObjectiveName,
                                raw?.LearningObjectiveName,
                                objectiveLabel
                            )
                            if (!metadataUpdates[questionBankId]) {
                                metadataUpdates[questionBankId] = {}
                            }
                            if (normalizedDifficulty.label) {
                                metadataUpdates[questionBankId].difficultyLabel = normalizedDifficulty.label
                            }
                            if (typeof normalizedDifficulty.value === 'number') {
                                metadataUpdates[questionBankId].difficultyValue = normalizedDifficulty.value
                            }
                            if (learningObjectiveLabel) {
                                metadataUpdates[questionBankId].learningObjectiveName = learningObjectiveLabel
                            }
                        })
                    })
                    if (Object.keys(metadataUpdates).length > 0) {
                        setQuestionMetadataMap((prev) => {
                            const next = { ...prev }
                            Object.entries(metadataUpdates).forEach(([key, meta]) => {
                                const id = Number(key)
                                const existing = next[id] || {}
                                next[id] = {
                                    difficultyLabel: meta.difficultyLabel ?? existing.difficultyLabel ?? null,
                                    difficultyValue:
                                        typeof meta.difficultyValue === 'number'
                                            ? meta.difficultyValue
                                            : typeof existing.difficultyValue === 'number'
                                                ? existing.difficultyValue
                                                : null,
                                    learningObjectiveName: meta.learningObjectiveName ?? existing.learningObjectiveName ?? null,
                                }
                            })
                            return next
                        })
                    }
                }
            } catch (err) {
                console.error(err)
                setQuestionPool(null)
                setQuestionPoolIncludesUsedQuestions(false)
            } finally {
                setLoading(false)
            }
        },
        [selectedTagIds]
    )

    const refreshAutoAddOutcomePool = useCallback(
        async (includeUsed: boolean) => {
            if (!semesterCourseId) {
                setAutoAddOutcomePool(null)
                return
            }
            try {
                setAutoAddLoading(true)
                const pool = await getExamQuestionPool(
                    semesterCourseId,
                    undefined,
                    examId ?? undefined,
                    undefined,
                    includeUsed
                )
                setAutoAddOutcomePool(pool || null)
            } catch (error) {
                console.error(error)
                setAutoAddOutcomePool(null)
            } finally {
                setAutoAddLoading(false)
            }
        },
        [examId, semesterCourseId]
    )

    const applyAssignmentsResponse = useCallback(
        (data: ExamQuestionAssignmentsResponse | null | undefined): boolean => {
            if (!data) {
                setAssignments(null)
                setGroupState([])
                setPointInputs({})
                setHasExistingBooklets(false)
                setHasBookletAnswers(false)
                return false
            }

            setAssignments(data)
            setGroupState(data.groups || [])

            const assignmentTerm =
                pickFirstString(
                    (data as any)?.academicTerm,
                    (data as any)?.semesterName,
                    (data as any)?.semesterTerm,
                    (data as any)?.termName
                ) || extractAcademicTermFromSemesterCourse((data as any)?.SemesterCourse || (data as any)?.semesterCourse)

            if (assignmentTerm) {
                setAcademicTerm(assignmentTerm)
            }

            const initialPoints: Record<number, string> = {}
            const metadataUpdates: Record<number, QuestionMetadata> = {}

            data.groups?.forEach((group) => {
                const groupLabel = pickFirstString(group.groupName, (group as any)?.GroupName)
                group.questions.forEach((question) => {
                    const pointValue =
                        typeof question.point === 'number' && !Number.isNaN(question.point)
                            ? (Math.round(Number(question.point) * 100) / 100).toFixed(2)
                            : ''
                    initialPoints[question.examQuestionId] = pointValue

                    const questionBankId = Number(question.questionBankId)
                    if (!Number.isFinite(questionBankId)) {
                        return
                    }

                    const raw = question as any
                    const difficultyLabelCandidate = pickFirstString(
                        raw?.difficultyName,
                        raw?.DifficultyName,
                        raw?.difficultyLevelName,
                        raw?.DifficultyLevelName,
                        typeof raw?.difficulty === 'string' ? raw.difficulty : undefined,
                        typeof raw?.Difficulty === 'string' ? raw.Difficulty : undefined
                    )
                    const difficultyValueCandidate = pickFirstNumber(
                        raw?.difficultyLevel,
                        raw?.DifficultyLevel,
                        typeof raw?.difficulty === 'number' ? raw.difficulty : undefined,
                        typeof raw?.Difficulty === 'number' ? raw.Difficulty : undefined
                    )
                    const normalizedDifficulty = normalizeDifficultyDetails(
                        difficultyLabelCandidate,
                        difficultyValueCandidate
                    )
                    const learningObjectiveLabel = pickFirstString(
                        question.learningObjectiveName,
                        raw?.LearningObjectiveName,
                        groupLabel
                    )

                    if (!metadataUpdates[questionBankId]) {
                        metadataUpdates[questionBankId] = {}
                    }
                    if (normalizedDifficulty.label) {
                        metadataUpdates[questionBankId].difficultyLabel = normalizedDifficulty.label
                    }
                    if (typeof normalizedDifficulty.value === 'number') {
                        metadataUpdates[questionBankId].difficultyValue = normalizedDifficulty.value
                    }
                    if (learningObjectiveLabel) {
                        metadataUpdates[questionBankId].learningObjectiveName = learningObjectiveLabel
                    }
                })
            })

            setPointInputs(initialPoints)

            if (Object.keys(metadataUpdates).length > 0) {
                setQuestionMetadataMap((prev) => {
                    const next = { ...prev }
                    Object.entries(metadataUpdates).forEach(([key, meta]) => {
                        const id = Number(key)
                        const existing = next[id] || {}
                        next[id] = {
                            difficultyLabel: meta.difficultyLabel ?? existing.difficultyLabel ?? null,
                            difficultyValue:
                                typeof meta.difficultyValue === 'number'
                                    ? meta.difficultyValue
                                    : typeof existing.difficultyValue === 'number'
                                        ? existing.difficultyValue
                                        : null,
                            learningObjectiveName: meta.learningObjectiveName ?? existing.learningObjectiveName ?? null,
                        }
                    })
                    return next
                })
            }

            if (!semesterCourseId) {
                setSemesterCourseId(data.semesterCourseId)
            }

            if (data?.examName) {
                setExamName(data.examName)
            }

            if (data?.bookletCodes && data.bookletCodes.length) {
                applyIncomingBookletCodes(data.bookletCodes)
            }

            if (typeof data?.lastGeneratedAt !== 'undefined') {
                setLastGeneratedAt(data.lastGeneratedAt || null)
            }

            setHasExistingBooklets(Boolean(data?.hasExistingBooklets))
            setHasBookletAnswers(Boolean(data?.hasBookletAnswers))

            return true
        },
        [applyIncomingBookletCodes, semesterCourseId]
    )

    const loadAssignments = useCallback(
        async (targetExamId: number) => {
            try {
                const data = await getExamAssignments(targetExamId)
                applyAssignmentsResponse(data)
            } catch (err) {
                console.error(err)
                applyAssignmentsResponse(null)
            }
        },
        [applyAssignmentsResponse]
    )

    const handleGenerateBooklets = useCallback(async () => {
        if (!examId || !canAdjustBookletSettings) {
            return
        }

        const shouldWarn = hasExistingBooklets || hasBookletAnswers
        if (shouldWarn) {
            const result = await Swal.fire({
                title: 'Kitapçıkları Yeniden Oluştur',
                text: 'Önceki kitapçıklar silinecektir. Basılı ve uygulanmış sınav varsa bu işlemi yapmayınız.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Evet, devam et',
                cancelButtonText: 'Vazgeç',
                reverseButtons: true,
            })

            if (!result.isConfirmed) {
                return
            }
        }

        try {
            setBookletLoading(true)
            const layoutPayload = buildLayoutPayload()
            const response = await generateManualExamBooklets({
                examId,
                bookletCount: displayedBookletCount,
                layoutSettings: layoutPayload,
                shuffleSharedContentQuestions,
                sharedContentShuffleStates: buildSharedContentShufflePayload(),
            })
            if (response) {
                const incoming =
                    response.bookletCodes && response.bookletCodes.length
                        ? response.bookletCodes
                        : [DEFAULT_BOOKLET_CODES[0]]
                applyIncomingBookletCodes(incoming)
                setLastGeneratedAt(response.lastGeneratedAt || null)
                setHasExistingBooklets(Boolean(response.hasExistingBooklets))
                setHasBookletAnswers(Boolean(response.hasBookletAnswers))
            } else {
                await loadManualSettings(examId)
            }
            await loadAssignments(examId)
            setAnswerKeys({})
            setAnswerKeyGeneratedAt(null)
            Swal.fire('Başarılı', 'Kitapçıklar oluşturuldu.', 'success')
        } catch (err: any) {
            console.error(err)
            const message = err?.response?.data || 'Kitapçıklar oluşturulamadı.'
            Swal.fire('Hata', message, 'error')
        } finally {
            setBookletLoading(false)
        }
    }, [
        examId,
        canAdjustBookletSettings,
        hasBookletAnswers,
        hasExistingBooklets,
        applyIncomingBookletCodes,
        loadAssignments,
        loadManualSettings,
        buildLayoutPayload,
        displayedBookletCount,
        buildSharedContentShufflePayload,
        shuffleSharedContentQuestions,
    ])

    useEffect(() => {
        loadExamTypes()
    }, [loadExamTypes])

    useEffect(() => {
        loadAvailableTags()
    }, [loadAvailableTags])

    useEffect(() => {
        if (!isAutoAddModalOpen || autoAddSelectedTagId !== null) {
            return
        }
        const firstTagId = normalizedTagOptions.find((tag) => Number.isFinite(tag.id))?.id ?? null
        if (firstTagId !== null) {
            setAutoAddSelectedTagId(firstTagId)
        }
    }, [autoAddSelectedTagId, isAutoAddModalOpen, normalizedTagOptions])

    useEffect(() => {
        if (!isAutoAddModalOpen || autoAddTab !== 'outcome') {
            return
        }
        refreshAutoAddOutcomePool(autoAddOutcomeIncludeUsed)
    }, [autoAddOutcomeIncludeUsed, autoAddTab, isAutoAddModalOpen, refreshAutoAddOutcomePool])

    useEffect(() => {
        if (!isAutoAddModalOpen || autoAddTab !== 'outcome') {
            return
        }
        const firstObjectiveId =
            autoAddOutcomePool?.learningObjectives?.find((objective) => Number.isFinite((objective as any)?.id))
                ?.id ?? null
        if (typeof firstObjectiveId === 'number') {
            setAutoAddOutcomeRows((prev) => {
                if (prev.length === 0) {
                    return [{ objectiveId: firstObjectiveId, count: '' }]
                }
                let updated = false
                const next = prev.map((row, index) => {
                    if (row.objectiveId === null && index === 0) {
                        updated = true
                        return { ...row, objectiveId: firstObjectiveId }
                    }
                    return row
                })
                return updated ? next : prev
            })
        }
    }, [autoAddOutcomePool, autoAddTab, isAutoAddModalOpen])

    useEffect(() => {
        if (typeof locationState?.isBoard === 'boolean') {
            setIsBoardCourse(Boolean(locationState.isBoard))
        }
    }, [locationState?.isBoard])

    useEffect(() => {
        if (typeof window === 'undefined') {
            return
        }
        window.localStorage.setItem(
            AUTO_CALCULATE_POINTS_STORAGE_KEY,
            autoCalculatePoints ? 'true' : 'false'
        )
    }, [autoCalculatePoints])

    useEffect(() => {
        if (typeof window === 'undefined') {
            return
        }

        const handleResize = () => {
            setIsCompactViewport(window.innerWidth < 992)
        }

        handleResize()
        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    useEffect(() => {
        if (!selectedQuestion) {
            setIsPreviewModalOpen(false)
        }
    }, [selectedQuestion])

    useEffect(() => {
        let isMounted = true

        if (!examId) {
            setIsInitialLoading(false)
            return () => {
                isMounted = false
            }
        }

        setIsInitialLoading(true)

        const initialize = async () => {
            try {
                await Promise.all([
                    loadExamDetails(examId),
                    loadAssignments(examId),
                    loadManualSettings(examId),
                ])
            } finally {
                if (isMounted) {
                    setIsInitialLoading(false)
                }
            }
        }

        initialize()

        return () => {
            isMounted = false
        }
    }, [examId, loadAssignments, loadExamDetails, loadManualSettings])

    useEffect(() => {
        loadTenantSettings()
    }, [loadTenantSettings])

    useEffect(() => {
        if (semesterCourseId) {
            loadDepartments(semesterCourseId)
        }
    }, [semesterCourseId, loadDepartments])

    useEffect(() => {
        if (semesterCourseId) {
            loadQuestionPool(semesterCourseId, selectedDepartmentId, examId, showPreviouslyUsedQuestions)
        }
    }, [semesterCourseId, selectedDepartmentId, examId, loadQuestionPool, showPreviouslyUsedQuestions])

    useEffect(() => {
        let isMounted = true

        const loadCourseShareInfo = async () => {
            if (!semesterCourseId) {
                if (isMounted) {
                    setCourseShareInfo(null)
                    setSelectedShareCourseIds([])
                }
                return
            }

            try {
                setLoadingCourseShareInfo(true)
                const info = await getManualExamCourseShareInfo(semesterCourseId)
                if (!isMounted) {
                    return
                }

                setCourseShareInfo(info)

                const idSet = new Set<number>()
                info?.selectedCourses?.forEach((course) => {
                    if (course && typeof course.semesterCourseId === 'number') {
                        idSet.add(course.semesterCourseId)
                    }
                })

                idSet.add(semesterCourseId)

                setSelectedShareCourseIds(Array.from(idSet))
            } catch (error: any) {
                console.error(error)
                if (isMounted) {
                    setCourseShareInfo(null)
                    setSelectedShareCourseIds([semesterCourseId].filter((id): id is number => typeof id === 'number'))
                    const message = error?.response?.data || 'Birleştirme bilgileri alınamadı.'
                    Swal.fire('Hata', message, 'error')
                }
            } finally {
                if (isMounted) {
                    setLoadingCourseShareInfo(false)
                }
            }
        }

        loadCourseShareInfo()

        return () => {
            isMounted = false
        }
    }, [semesterCourseId])

    useEffect(() => {
        loadSemesterCourseInfo(semesterCourseId)
    }, [semesterCourseId, loadSemesterCourseInfo])

    useEffect(() => {
        if (!semesterCourseId || typeof selectedExamType !== 'number' || Number.isNaN(selectedExamType)) {
            return
        }

        const requestId = examNameRequestIdRef.current + 1
        examNameRequestIdRef.current = requestId

        getAutomaticExamName(semesterCourseId, selectedExamType)
            .then((generatedName) => {
                if (examNameRequestIdRef.current !== requestId) {
                    return
                }
                if (
                    !isExamNameManuallyEdited &&
                    typeof generatedName === 'string' &&
                    generatedName.trim().length > 0
                ) {
                    setExamName(generatedName)
                }
            })
            .catch(() => {
                /* ignore errors, keep existing value */
            })
    }, [semesterCourseId, selectedExamType, isExamNameManuallyEdited])

    useEffect(() => {
        if (!assignments) {
            const incomingCourseName =
                (typeof locationState?.boardModuleName === 'string' && locationState.boardModuleName) ||
                (typeof locationState?.courseName === 'string' && locationState.courseName) ||
                (typeof locationState?.name === 'string' && locationState.name) ||
                ''

            if (incomingCourseName && incomingCourseName !== courseName) {
                setCourseName(incomingCourseName)
            }

            const locationTerm =
                pickFirstString(
                    locationState?.academicTerm,
                    locationState?.semesterName,
                    locationState?.semesterTerm,
                    locationState?.termName
                ) || extractAcademicTermFromSemesterCourse(locationState?.semesterCourse || locationState?.SemesterCourse)

            if (locationTerm && locationTerm !== academicTerm) {
                setAcademicTerm(locationTerm)
            }
        }
    }, [assignments, locationState, courseName, academicTerm])

    const shareCourseOptions = useMemo<ManualExamCourseShareCourse[]>(() => {
        const courseMap = new Map<number, ManualExamCourseShareCourse>()

        const mergeCourse = (course?: ManualExamCourseShareCourse | null, lockedOverride?: boolean) => {
            if (!course || typeof course.semesterCourseId !== 'number') {
                return
            }

            const existing = courseMap.get(course.semesterCourseId)
            const isLocked = lockedOverride ?? existing?.isLocked ?? course.isLocked ?? false
            const isPrimary =
                existing?.isPrimary ?? course.isPrimary ?? (semesterCourseId === course.semesterCourseId)

            courseMap.set(course.semesterCourseId, {
                ...(existing ?? {}),
                ...course,
                isLocked,
                isPrimary,
            })
        }

        courseShareInfo?.selectedCourses?.forEach((course) => mergeCourse(course, true))
        courseShareInfo?.availableCourses?.forEach((course) => mergeCourse(course))

        if (semesterCourseId && typeof semesterCourseId === 'number' && !courseMap.has(semesterCourseId)) {
            mergeCourse(
                {
                    semesterCourseId,
                    courseName: courseName || undefined,
                    semesterCourseName: courseName || undefined,
                    instructorName: null,
                    isPrimary: true,
                    isLocked: true,
                },
                true
            )
        }

        return Array.from(courseMap.values()).sort((a, b) => {
            const aPriority = a.isPrimary ? 0 : a.isLocked ? 1 : 2
            const bPriority = b.isPrimary ? 0 : b.isLocked ? 1 : 2
            if (aPriority !== bPriority) {
                return aPriority - bPriority
            }

            const aCode = (a.courseCode || '').trim()
            const bCode = (b.courseCode || '').trim()
            if (aCode && bCode) {
                const comparison = aCode.localeCompare(bCode, 'tr', { sensitivity: 'base' })
                if (comparison !== 0) {
                    return comparison
                }
            } else if (aCode || bCode) {
                return aCode.localeCompare(bCode, 'tr', { sensitivity: 'base' })
            }

            const aName = (a.courseName || a.semesterCourseName || '').trim()
            const bName = (b.courseName || b.semesterCourseName || '').trim()
            return aName.localeCompare(bName, 'tr', { sensitivity: 'base' })
        })
    }, [courseShareInfo, semesterCourseId, courseName])

    useEffect(() => {
        setSelectedShareCourseIds((prev) => {
            const allowedIds = new Set(
                shareCourseOptions
                    .map((course) => course.semesterCourseId)
                    .filter((id): id is number => typeof id === 'number')
            )

            if (semesterCourseId && typeof semesterCourseId === 'number') {
                allowedIds.add(semesterCourseId)
            }

            if (allowedIds.size === 0) {
                return semesterCourseId && typeof semesterCourseId === 'number' ? [semesterCourseId] : []
            }

            const selectedSet = new Set(prev.filter((id) => allowedIds.has(id)))

            if (semesterCourseId && typeof semesterCourseId === 'number') {
                selectedSet.add(semesterCourseId)
            }

            const ordered = shareCourseOptions
                .map((course) => course.semesterCourseId)
                .filter((id): id is number => typeof id === 'number' && selectedSet.has(id))

            if (ordered.length === prev.length && ordered.every((id, index) => prev[index] === id)) {
                return prev
            }

            return ordered.length > 0
                ? ordered
                : semesterCourseId && typeof semesterCourseId === 'number'
                    ? [semesterCourseId]
                    : []
        })
    }, [shareCourseOptions, semesterCourseId])

    const lockedSharedCourseIds = useMemo(() => {
        return shareCourseOptions
            .filter((course) => course.isLocked && typeof course.semesterCourseId === 'number')
            .map((course) => course.semesterCourseId as number)
    }, [shareCourseOptions])

    const toggleSharedCourse = useCallback(
        (courseId: number) => {
            if (!Number.isFinite(courseId) || lockedSharedCourseIds.includes(courseId)) {
                return
            }

            setSelectedShareCourseIds((prev) => {
                if (prev.includes(courseId)) {
                    return prev.filter((id) => id !== courseId)
                }
                return [...prev, courseId]
            })
        },
        [lockedSharedCourseIds]
    )

    const formatShareCourseLabel = useCallback((course?: ManualExamCourseShareCourse | null) => {
        if (!course) {
            return 'Ders'
        }

        const parts: string[] = []
        const code = typeof course.courseCode === 'string' ? course.courseCode.trim() : ''
        const name = typeof course.courseName === 'string' ? course.courseName.trim() : ''
        if (code && name) {
            parts.push(`${code} - ${name}`)
        } else if (name) {
            parts.push(name)
        } else if (code) {
            parts.push(code)
        }

        const instructor = typeof course.instructorName === 'string' ? course.instructorName.trim() : ''
        if (instructor) {
            parts.push(instructor)
        }

        const sectionName =
            typeof course.semesterCourseName === 'string' ? course.semesterCourseName.trim() : ''
        if (sectionName && sectionName !== name) {
            parts.push(sectionName)
        }

        if (parts.length === 0) {
            return 'Ders'
        }

        return parts.join(' / ')
    }, [])

    const handleExamNameChange = useCallback((value: string) => {
        setExamName(value)
        setIsExamNameManuallyEdited(true)
    }, [])

    const handleCreateExam = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault()
            if (!semesterCourseId || selectedExamType === null) {
                Swal.fire('Uyarı', 'Lütfen sınav türünü seçiniz.', 'warning')
                return
            }
            if (typeof examName !== 'string' || examName.trim().length === 0) {
                Swal.fire('Uyarı', 'Sınav başlığı oluşturulamadı.', 'warning')
                return
            }
            if (isExamDeliveryMethodEnabled && !selectedExamDeliveryMethod) {
                Swal.fire('UyarÄ±', 'LÃ¼tfen sÄ±navÄ±n uygulanÄ±ÅŸ ÅŸeklini seÃ§iniz.', 'warning')
                return
            }
            let durationValue: number | undefined
            if (examDuration !== '') {
                const parsed = Number(examDuration)
                if (!Number.isNaN(parsed) && Number.isFinite(parsed) && parsed >= 0) {
                    durationValue = parsed
                }
            }

            const payload: CreateManualExamPayload = {
                semesterCourseId,
                examTypeId: selectedExamType,
                contributionPercentage: contribution,
                instructions,
                name: examName,
                questionCount: plannedQuestionCount || undefined,
                durationInMinutes: durationValue,
                examDeliveryMethod: isExamDeliveryMethodEnabled ? selectedExamDeliveryMethod : null,
            }

            const sharedIds = selectedShareCourseIds
                .filter((id) => Number.isFinite(id) && id > 0 && id !== semesterCourseId)
                .map((id) => Number(id))

            if (sharedIds.length > 0) {
                payload.sharedSemesterCourseIds = Array.from(new Set(sharedIds))
            }
            try {
                setSubmitting(true)
                const response = await createManualExam(payload)
                const createdId =
                    response?.data?.data?.id ??
                    response?.data?.data?.Id ??
                    response?.data?.id ??
                    response?.data?.Id
                if (createdId) {
                    Swal.fire('Başarılı', 'Sınav oluşturuldu.', 'success')
                    setExamId(createdId)
                    if (semesterCourseId) {
                        const normalizedIds = [semesterCourseId, ...sharedIds]
                            .filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
                        const normalizedSet = new Set<number>(normalizedIds)

                        setSelectedShareCourseIds(Array.from(normalizedSet))

                        setCourseShareInfo((previous) => {
                            const prev = previous || {
                                semesterCourseId: semesterCourseId,
                                selectedCourses: [],
                                availableCourses: [],
                            }

                            const existingSelected = prev.selectedCourses || []
                            const existingAvailable = prev.availableCourses || []

                            const seenIds = new Set<number>()
                            const nextSelected: ManualExamCourseShareCourse[] = []

                            existingSelected.forEach((course) => {
                                const id = course?.semesterCourseId
                                if (typeof id !== 'number' || !normalizedSet.has(id) || seenIds.has(id)) {
                                    return
                                }
                                seenIds.add(id)
                                nextSelected.push({
                                    ...course,
                                    semesterCourseId: id,
                                    isPrimary: course?.isPrimary ?? id === semesterCourseId,
                                    isLocked: true,
                                })
                            })

                            normalizedIds.forEach((id) => {
                                if (seenIds.has(id)) {
                                    return
                                }
                                const fallback = existingAvailable.find((course) => course?.semesterCourseId === id)
                                seenIds.add(id)
                                nextSelected.push({
                                    ...(fallback || { semesterCourseId: id }),
                                    semesterCourseId: id,
                                    isPrimary: fallback?.isPrimary ?? id === semesterCourseId,
                                    isLocked: true,
                                })
                            })

                            const nextAvailable = existingAvailable.filter((course) => {
                                const id = course?.semesterCourseId
                                return !(typeof id === 'number' && seenIds.has(id))
                            })

                            return {
                                semesterCourseId: prev.semesterCourseId ?? semesterCourseId,
                                selectedCourses: nextSelected,
                                availableCourses: nextAvailable,
                            }
                        })
                    }
                    if (selectedMaterialFile) {
                        await uploadSupplementaryMaterial(createdId, selectedMaterialFile, false)
                    }
                    await loadAssignments(createdId)
                } else {
                    Swal.fire('Bilgi', 'Sınav oluşturuldu ancak kimlik alınamadı.', 'info')
                }
            } catch (err: any) {
                console.error(err)
                const message = err?.response?.data || 'Sınav oluşturulamadı.'
                Swal.fire('Hata', message, 'error')
            } finally {
                setSubmitting(false)
            }
        },
        [
            semesterCourseId,
            selectedExamType,
            contribution,
            instructions,
            examName,
            plannedQuestionCount,
            examDuration,
            isExamDeliveryMethodEnabled,
            selectedExamDeliveryMethod,
            selectedShareCourseIds,
            loadAssignments,
            selectedMaterialFile,
            uploadSupplementaryMaterial,
        ]
    )

    const collectSharedGroupQuestions = useCallback(
        (question: ExamQuestionPoolQuestion) => {
            if (!questionPool) {
                return [question]
            }
            if (!question.sharedContentId) {
                return [question]
            }
            const items: ExamQuestionPoolQuestion[] = []
            questionPool.learningObjectives.forEach((objective) => {
                objective.questions.forEach((q) => {
                    if (q.sharedContentId === question.sharedContentId) {
                        items.push(q)
                    }
                })
            })
            return items.length ? items : [question]
        },
        [questionPool]
    )

    const selectCandidatesForCount = useCallback(
        (candidates: AutoSelectionCandidate[], targetCount: number) => {
            if (!Array.isArray(candidates) || candidates.length === 0 || targetCount <= 0) {
                return {
                    selected: [] as AutoSelectionCandidate[],
                    totalSelected: 0,
                }
            }

            const shuffled = shuffleArray(candidates)
            const selected: AutoSelectionCandidate[] = []
            let remaining = targetCount

            shuffled.forEach((candidate) => {
                if (remaining <= 0) {
                    return
                }
                if (candidate.totalQuestions <= remaining) {
                    selected.push(candidate)
                    remaining -= candidate.totalQuestions
                }
            })

            return {
                selected,
                totalSelected: targetCount - remaining,
            }
        },
        []
    )

    const buildDifficultyAvailability = useCallback((candidates: AutoSelectionCandidate[]) => {
        const availability = new Map<number, number>()
        candidates.forEach((candidate) => {
            const difficultyValue =
                typeof candidate.difficulty === 'number'
                    ? candidate.difficulty
                    : candidate.questions.every((question) => typeof question.difficulty === 'number')
                        ? (candidate.questions[0].difficulty as number)
                        : null

            if (difficultyValue && Number.isFinite(difficultyValue)) {
                availability.set(difficultyValue, (availability.get(difficultyValue) || 0) + candidate.totalQuestions)
            }
        })
        return availability
    }, [])

    const buildAutoAddPayload = useCallback(
        (selectedCandidates: AutoSelectionCandidate[]): AddExamQuestionsPayload | null => {
            if (!examId) {
                return null
            }

            const items = selectedCandidates.flatMap((candidate) =>
                candidate.questions.map((question) => ({
                    questionBankId: question.questionBankId,
                    groupType: question.groupType,
                    groupId: question.groupId,
                    groupName: question.groupName,
                    sharedContentId: question.sharedContentId ?? undefined,
                    learningObjectiveId: question.learningObjectiveId ?? undefined,
                    optionOrder: question.optionOrder,
                }))
            )

            if (!items.length) {
                return null
            }

            return {
                examId,
                items,
            }
        },
        [examId]
    )

    const handleAutoAddResponse = useCallback(
        async (response: any) => {
            const rawPayload = response?.data as ExamQuestionAssignmentsResponse | null | undefined
            const payload =
                rawPayload && typeof rawPayload === 'object' && Array.isArray((rawPayload as any)?.groups)
                    ? (rawPayload as ExamQuestionAssignmentsResponse)
                    : null
            const applied = applyAssignmentsResponse(payload)
            if (!applied && examId) {
                await loadAssignments(examId)
            }
        },
        [applyAssignmentsResponse, examId, loadAssignments]
    )

    const handleAddQuestion = useCallback(
        async (question: ExamQuestionPoolQuestion) => {
            if (!canEditExamContent) {
                Swal.fire('Uyarı', 'Sınav durumu nedeniyle düzenleme yapılamaz.', 'warning')
                return
            }
            if (!examId || selectedDepartmentId === null || selectedDepartmentId === undefined) {
                Swal.fire('Uyarı', 'Önce sınavı oluşturmalısınız.', 'warning')
                return
            }
            const selectedGroupName =
                findDepartmentName(selectedDepartmentId) ||
                (isBoardCourse ? 'Grup' : 'Ders Öğrenme Çıktısı')
            const groupType = isBoardCourse ? 'department' : 'outcome'
            const groupedQuestions = collectSharedGroupQuestions(question)
            const items = groupedQuestions
                .filter((q) => !existingQuestionIds.has(q.questionBankId))
                .map((q) => {
                    const normalizedLearningObjectiveId = Number.isFinite(q.learningObjectiveId)
                        ? Number(q.learningObjectiveId)
                        : null
                    const normalizedDepartmentId = Number.isFinite(q.departmentId)
                        ? Number(q.departmentId)
                        : null
                    const resolvedGroupId = isBoardCourse
                        ? normalizedDepartmentId ?? selectedDepartmentId
                        : normalizedLearningObjectiveId ?? selectedDepartmentId

                    const resolvedGroupName =
                        findDepartmentName(resolvedGroupId) ||
                        (isBoardCourse
                            ? q.departmentName || selectedGroupName
                            : q.learningObjectiveName || selectedGroupName)

                    return {
                        questionBankId: q.questionBankId,
                        groupType,
                        groupId: resolvedGroupId,
                        groupName: resolvedGroupName,
                        sharedContentId: q.sharedContentId || undefined,
                        learningObjectiveId: normalizedLearningObjectiveId ?? undefined,
                        optionOrder: q.questionItems
                            .slice()
                            .sort((a, b) => (a.order || 0) - (b.order || 0) || a.id - b.id)
                            .map((item) => item.id),
                    }
                })
                .filter((item): item is NonNullable<typeof item> & { groupId: number } => Number.isFinite(item.groupId))

            if (!items.length) {
                Swal.fire('Bilgi', 'Seçilen sorular zaten sınava eklenmiş.', 'info')
                return
            }

            try {
                setSubmitting(true)
                const response = await addExamQuestions({ examId, items, autoCalculatePoints })
                const rawPayload = response?.data as ExamQuestionAssignmentsResponse | null | undefined
                const payload =
                    rawPayload && typeof rawPayload === 'object' && Array.isArray((rawPayload as any)?.groups)
                        ? (rawPayload as ExamQuestionAssignmentsResponse)
                        : null
                const applied = applyAssignmentsResponse(payload)
                Swal.fire('Başarılı', 'Sorular sınava eklendi.', 'success')
                if (!applied) {
                    await loadAssignments(examId)
                }
            } catch (err: any) {
                console.error(err)
                const message = err?.response?.data || 'Sorular eklenemedi.'
                Swal.fire('Hata', message, 'error')
            } finally {
                setSubmitting(false)
            }
        },
        [
            examId,
            selectedDepartmentId,
            collectSharedGroupQuestions,
            existingQuestionIds,
            findDepartmentName,
            loadAssignments,
            applyAssignmentsResponse,
            canEditExamContent,
            isBoardCourse,
            autoCalculatePoints,
        ]
    )

    const handlePointInputChange = useCallback((examQuestionId: number, value: string) => {
        if (!canEditExamContent) {
            return
        }
        setPointInputs((prev) => ({
            ...prev,
            [examQuestionId]: value,
        }))
    }, [canEditExamContent])

    const handlePointInputBlur = useCallback((examQuestionId: number) => {
        setPointInputs((prev) => {
            const rawValue = prev[examQuestionId]
            if (typeof rawValue !== 'string') {
                return prev
            }
            const normalized = rawValue.replace(',', '.').trim()
            if (normalized === '') {
                return {
                    ...prev,
                    [examQuestionId]: '',
                }
            }
            const numeric = Number.parseFloat(normalized)
            if (Number.isNaN(numeric) || !Number.isFinite(numeric)) {
                return {
                    ...prev,
                    [examQuestionId]: '',
                }
            }
            const formatted = (Math.round(numeric * 100) / 100).toFixed(2)
            return {
                ...prev,
                [examQuestionId]: formatted,
            }
        })
    }, [])

    const handleSavePoints = useCallback(async () => {
        if (!examId) {
            return
        }
        if (!canEditExamContent) {
            Swal.fire('Uyarı', 'Sınav durumu nedeniyle düzenleme yapılamaz.', 'warning')
            return
        }
        if (!groupState.length) {
            Swal.fire('Bilgi', 'Güncellenecek soru bulunmuyor.', 'info')
            return
        }
        if (invalidPointIds.size > 0) {
            Swal.fire('Uyarı', 'Lütfen tüm puan alanlarını geçerli sayılarla doldurun.', 'warning')
            return
        }
        if (!isTotalValid) {
            Swal.fire('Uyarı', 'Puan toplamı 100 olmalıdır.', 'warning')
            return
        }

        const items = groupState.flatMap((group) =>
            group.questions.map((question) => {
                const rawValue = pointInputs[question.examQuestionId] ?? '0'
                const normalized = rawValue.replace(',', '.')
                const numeric = Number.parseFloat(normalized)
                const rounded = Math.round(numeric * 100) / 100
                return {
                    examQuestionId: question.examQuestionId,
                    point: Number(rounded.toFixed(2)),
                }
            })
        )

        const payload: UpdateExamQuestionPointsPayload = {
            examId,
            items,
        }

        try {
            setPointSaving(true)
            await updateExamQuestionPoints(payload)
            Swal.fire('Başarılı', 'Puanlar güncellendi.', 'success')
            await loadAssignments(examId)
        } catch (err: any) {
            console.error(err)
            const message = err?.response?.data || 'Puanlar güncellenemedi.'
            Swal.fire('Hata', message, 'error')
        } finally {
            setPointSaving(false)
        }
    }, [
        examId,
        canEditExamContent,
        groupState,
        invalidPointIds,
        isTotalValid,
        pointInputs,
        loadAssignments,
    ])

    const handleMoveQuestion = useCallback(
        async (groupIndex: number, questionIndex: number, direction: 'up' | 'down') => {
            if (!examId || !canEditExamContent) return
            setGroupState((prev) => {
                const cloned = prev.map((group) => ({ ...group, questions: [...group.questions] }))
                const group = cloned[groupIndex]
                if (!group) {
                    return prev
                }
                const targetIndex = direction === 'up' ? questionIndex - 1 : questionIndex + 1
                if (targetIndex < 0 || targetIndex >= group.questions.length) {
                    return prev
                }
                ;[group.questions[questionIndex], group.questions[targetIndex]] = [
                    group.questions[targetIndex],
                    group.questions[questionIndex],
                ]
                const payload: ReorderExamQuestionsPayload = {
                    examId,
                    groups: cloned.map((g) => ({
                        groupType: g.groupType,
                        groupId: g.groupId || 0,
                        groupName: g.groupName,
                        questions: g.questions.map((q) => ({
                            examQuestionId: q.examQuestionId,
                            sequenceGroup: q.sequenceGroup || undefined,
                        })),
                    })),
                }
                reorderExamQuestions(payload)
                    .then(() => {
                        loadAssignments(examId)
                        setAnswerKeys({})
                        setAnswerKeyGeneratedAt(null)
                    })
                    .catch((err) => {
                        console.error(err)
                        Swal.fire('Hata', 'Sıralama güncellenemedi.', 'error')
                    })
                return cloned
            })
        },
        [canEditExamContent, examId, loadAssignments]
    )

    const handleRemoveQuestion = useCallback(
        async (question: ExamQuestionAssignment) => {
            if (!examId) return
            if (!canEditExamContent) {
                Swal.fire('Uyarı', 'Sınav durumu nedeniyle düzenleme yapılamaz.', 'warning')
                return
            }
            const hasManualBooklets =
                !!lastGeneratedAt ||
                !!(
                    assignments?.bookletQuestionOrder &&
                    Object.values(assignments.bookletQuestionOrder).some((order) => (order?.length || 0) > 0)
                )
            const confirm = await Swal.fire({
                title: 'Emin misiniz?',
                text: hasManualBooklets
                    ? 'Bu soruyu silerseniz mevcut kitapçıklar sıfırlanacaktır. Devam etmek istiyor musunuz?'
                    : 'Soruyu sınavdan kaldırmak istediğinize emin misiniz?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Evet',
                cancelButtonText: 'Vazgeç',
            })
            if (!confirm.isConfirmed) {
                return
            }
            try {
                await removeExamQuestion(question.examQuestionId)
                await loadAssignments(examId)
                setAnswerKeys({})
                setAnswerKeyGeneratedAt(null)
            } catch (err) {
                console.error(err)
                Swal.fire('Hata', 'Soru kaldırılamadı.', 'error')
            }
        },
        [examId, canEditExamContent, loadAssignments, assignments, lastGeneratedAt]
    )

    const handleAutoAddByTag = useCallback(async () => {
        if (!canEditExamContent) {
            Swal.fire('Uyarı', 'Sınav durumu nedeniyle düzenleme yapılamaz.', 'warning')
            return
        }

        if (!examId || !semesterCourseId) {
            Swal.fire('Uyarı', 'Soruları eklemek için önce sınavı oluşturmalısınız.', 'warning')
            return
        }

        if (!Number.isFinite(autoAddSelectedTagId)) {
            Swal.fire('Uyarı', 'Lütfen bir etiket seçiniz.', 'warning')
            return
        }

        const requestedDifficultyTotal = Object.values(normalizedDifficultyRequests).reduce(
            (sum, value) => sum + (Number.isFinite(value) ? Number(value) : 0),
            0
        )
        const desiredCountInput = Math.max(0, Math.floor(Number(autoAddTagCount) || 0))
        const desiredCount = requestedDifficultyTotal > 0 ? requestedDifficultyTotal : desiredCountInput
        if (desiredCount <= 0) {
            Swal.fire('Uyarı', 'Eklenecek soru sayısını giriniz.', 'warning')
            return
        }

        setAutoAddSubmitting(true)
        try {
            const pool = await getExamQuestionPool(
                semesterCourseId,
                undefined,
                examId,
                [Number(autoAddSelectedTagId)],
                autoAddIncludeUsed
            )

            const candidates = buildAutoSelectionCandidates(pool, autoAddIncludeUsed)
            const availableCount = candidates.reduce((sum, candidate) => sum + candidate.totalQuestions, 0)

            if (availableCount === 0) {
                Swal.fire('Uyarı', 'Seçilen etikete ait uygun soru bulunamadı.', 'warning')
                return
            }

            if (desiredCount > availableCount) {
                Swal.fire(
                    'Uyarı',
                    `Seçilen etikete ait yeterli soru bulunmuyor. Mevcut: ${availableCount}.`,
                    'warning'
                )
                return
            }

            const difficultyRequestEntries = Object.entries(normalizedDifficultyRequests).map(([key, value]) => ({
                difficulty: Number(key),
                count: Number(value),
            }))

            if (difficultyRequestEntries.length > 0) {
                const difficultyAvailability = buildDifficultyAvailability(candidates)
                for (const entry of difficultyRequestEntries) {
                    const available = difficultyAvailability.get(entry.difficulty) || 0
                    if (available < entry.count) {
                        const label =
                            DIFFICULTY_OPTIONS.find((option) => option.value === entry.difficulty)?.label ||
                            `Zorluk ${entry.difficulty}`
                        Swal.fire('Uyarı', `${label} zorlukta yeterli soru bulunamadı. Mevcut: ${available}.`, 'warning')
                        return
                    }
                }

                let remainingCandidates = [...candidates]
                const selected: AutoSelectionCandidate[] = []
                let totalSelected = 0

                for (const entry of difficultyRequestEntries) {
                    const remainingCount = Math.max(0, Math.floor(entry.count))
                    if (remainingCount <= 0) {
                        continue
                    }

                    const eligible = remainingCandidates.filter(
                        (candidate) =>
                            candidate.totalQuestions <= remainingCount &&
                            typeof candidate.difficulty === 'number' &&
                            candidate.difficulty === entry.difficulty
                    )

                    const { selected: picked, totalSelected: pickedCount } = selectCandidatesForCount(
                        eligible,
                        remainingCount
                    )

                    if (pickedCount < remainingCount) {
                        const label =
                            DIFFICULTY_OPTIONS.find((option) => option.value === entry.difficulty)?.label ||
                            `Zorluk ${entry.difficulty}`
                        Swal.fire(
                            'Uyarı',
                            `${label} zorluğunda istenen sayıda soru seçilemedi. Lütfen adedi azaltın.`,
                            'warning'
                        )
                        return
                    }

                    const pickedKeys = new Set(picked.map((item) => item.key))
                    remainingCandidates = remainingCandidates.filter((candidate) => !pickedKeys.has(candidate.key))
                    selected.push(...picked)
                    totalSelected += pickedCount
                }

                if (totalSelected < desiredCount) {
                    Swal.fire('Uyarı', 'Zorluk dağılımı için yeterli soru seçilemedi.', 'warning')
                    return
                }

                const payload = buildAutoAddPayload(selected)
                if (!payload) {
                    Swal.fire('Uyarı', 'Eklenecek soru bulunamadı.', 'warning')
                    return
                }

                const response = await addExamQuestions({ ...payload, autoCalculatePoints })
                await handleAutoAddResponse(response)
                Swal.fire('Başarılı', `${totalSelected} soru sınava eklendi.`, 'success')
                setIsAutoAddModalOpen(false)
                return
            }

            const { selected, totalSelected } = selectCandidatesForCount(candidates, desiredCount)

            if (!selected.length || totalSelected < desiredCount) {
                Swal.fire(
                    'Uyarı',
                    'Grup zorunlulukları nedeniyle istenen sayıda soru seçilemedi. Lütfen sayıyı güncelleyiniz.',
                    'warning'
                )
                return
            }

            const payload = buildAutoAddPayload(selected)
            if (!payload) {
                Swal.fire('Uyarı', 'Eklenecek soru bulunamadı.', 'warning')
                return
            }

            const response = await addExamQuestions({ ...payload, autoCalculatePoints })
            await handleAutoAddResponse(response)
            Swal.fire('Başarılı', `${totalSelected} soru sınava eklendi.`, 'success')
            setIsAutoAddModalOpen(false)
        } catch (error: any) {
            console.error(error)
            const message = error?.response?.data || 'Sorular eklenemedi.'
            Swal.fire('Hata', message, 'error')
        } finally {
            setAutoAddSubmitting(false)
        }
    }, [
        autoAddIncludeUsed,
        autoAddSelectedTagId,
        autoAddTagCount,
        normalizedDifficultyRequests,
        buildDifficultyAvailability,
        buildAutoAddPayload,
        buildAutoSelectionCandidates,
        canEditExamContent,
        examId,
        handleAutoAddResponse,
        selectCandidatesForCount,
        semesterCourseId,
        autoCalculatePoints,
    ])

    const handleAutoAddByOutcome = useCallback(async () => {
        if (!canEditExamContent) {
            Swal.fire('Uyarı', 'Sınav durumu nedeniyle düzenleme yapılamaz.', 'warning')
            return
        }

        if (!examId || !semesterCourseId) {
            Swal.fire('Uyarı', 'Soruları eklemek için önce sınavı oluşturmalısınız.', 'warning')
            return
        }

        const normalizedRows = autoAddOutcomeRows
            .map((row) => ({
                objectiveId: Number.isFinite(row.objectiveId) ? Number(row.objectiveId) : null,
                count: Math.max(0, Math.floor(Number(row.count) || 0)),
            }))
            .filter((row) => row.objectiveId && row.count > 0) as Array<{ objectiveId: number; count: number }>

        if (!normalizedRows.length) {
            Swal.fire('Uyarı', 'Lütfen eklenecek öğrenme çıktısı ve soru sayılarını belirtin.', 'warning')
            return
        }

        const aggregatedRequests = normalizedRows.reduce<Record<number, number>>((acc, row) => {
            if (!acc[row.objectiveId]) {
                acc[row.objectiveId] = 0
            }
            acc[row.objectiveId] += row.count
            return acc
        }, {})

        const requestEntries = Object.entries(aggregatedRequests).map(([key, value]) => ({
            objectiveId: Number(key),
            count: value,
        }))

        const difficultyRequestTotal = Object.values(normalizedDifficultyRequests).reduce(
            (sum, value) => sum + (Number.isFinite(value) ? Number(value) : 0),
            0
        )
        const totalRequested = requestEntries.reduce((sum, entry) => sum + entry.count, 0)
        if (difficultyRequestTotal > totalRequested && difficultyRequestTotal > 0) {
            Swal.fire(
                'Uyarı',
                'Zorluk dağılımı toplamı, eklemek istediğiniz soru sayısından fazla. Lütfen adetleri güncelleyiniz.',
                'warning'
            )
            return
        }

        setAutoAddSubmitting(true)
        try {
            const pool = await getExamQuestionPool(
                semesterCourseId,
                undefined,
                examId,
                undefined,
                autoAddOutcomeIncludeUsed
            )
            setAutoAddOutcomePool(pool || null)

            const candidates = buildAutoSelectionCandidates(pool, autoAddOutcomeIncludeUsed)
            const objectiveNameLookup = new Map<number, string>()
            pool?.learningObjectives?.forEach((objective) => {
                const id = Number(objective?.id)
                if (!Number.isFinite(id)) {
                    return
                }
                const label = pickFirstString(
                    objective?.name,
                    objective?.learningObjectiveName,
                    objective?.boardModuleName,
                    objective?.departmentName
                )
                if (label) {
                    objectiveNameLookup.set(id, label)
                }
            })

            const availableByObjective = new Map<number, number>()
            candidates.forEach((candidate) => {
                candidate.questions.forEach((question) => {
                    const objId = typeof question.learningObjectiveId === 'number' ? question.learningObjectiveId : null
                    if (objId !== null) {
                        availableByObjective.set(objId, (availableByObjective.get(objId) || 0) + 1)
                    }
                })
            })

            for (const entry of requestEntries) {
                const available = availableByObjective.get(entry.objectiveId) || 0
                if (available < entry.count) {
                    const name = objectiveNameLookup.get(entry.objectiveId) || 'Seçilen öğrenme çıktısı'
                    Swal.fire('Uyarı', `${name} için yeterli soru bulunamadı. Mevcut: ${available}.`, 'warning')
                    return
                }
            }

            const difficultyRequestEntries = Object.entries(normalizedDifficultyRequests).map(([key, value]) => ({
                difficulty: Number(key),
                count: Number(value),
            }))

            if (difficultyRequestEntries.length > 0) {
                const difficultyAvailability = buildDifficultyAvailability(candidates)
                for (const entry of difficultyRequestEntries) {
                    const available = difficultyAvailability.get(entry.difficulty) || 0
                    if (available < entry.count) {
                        const label =
                            DIFFICULTY_OPTIONS.find((option) => option.value === entry.difficulty)?.label ||
                            `Zorluk ${entry.difficulty}`
                        Swal.fire('Uyarı', `${label} zorlukta yeterli soru bulunamadı. Mevcut: ${available}.`, 'warning')
                        return
                    }
                }
            }

            let remainingCandidates = [...candidates]
            const selected: AutoSelectionCandidate[] = []
            let totalSelected = 0
            const usedQuestionIds = new Set<number>()
            const remainingDifficulty = { ...normalizedDifficultyRequests }

            for (const entry of requestEntries) {
                const eligible = remainingCandidates.filter(
                    (candidate) =>
                        candidate.questions.length > 0 &&
                        candidate.questions.every((question) => {
                            const objId = typeof question.learningObjectiveId === 'number' ? question.learningObjectiveId : null
                            return objId === entry.objectiveId && !usedQuestionIds.has(question.questionBankId)
                        })
                )

                if (eligible.length === 0) {
                    const name = objectiveNameLookup.get(entry.objectiveId) || 'Seçilen öğrenme çıktısı'
                    Swal.fire('Uyarı', `${name} için uygun soru bulunamadı.`, 'warning')
                    return
                }

                if (Object.keys(remainingDifficulty).length > 0) {
                    let objectiveRemaining = entry.count
                    const pickedForObjective: AutoSelectionCandidate[] = []

                    for (const diffEntry of Object.entries(remainingDifficulty)) {
                        if (objectiveRemaining <= 0) {
                            break
                        }
                        const diff = Number(diffEntry[0])
                        const diffRemaining = Math.max(0, Math.floor(Number(diffEntry[1]) || 0))
                        if (diffRemaining <= 0) {
                            continue
                        }
                        const targetCount = Math.min(diffRemaining, objectiveRemaining)
                        const diffEligible = eligible.filter(
                            (candidate) =>
                                candidate.totalQuestions <= targetCount &&
                                typeof candidate.difficulty === 'number' &&
                                candidate.difficulty === diff
                        )

                        if (!diffEligible.length) {
                            continue
                        }

                        const { selected: diffPicked, totalSelected: diffPickedCount } = selectCandidatesForCount(
                            diffEligible,
                            targetCount
                        )

                        if (diffPickedCount > 0) {
                            const pickedKeys = new Set(diffPicked.map((item) => item.key))
                            remainingCandidates = remainingCandidates.filter((candidate) => !pickedKeys.has(candidate.key))
                            diffPicked.forEach((candidate) =>
                                candidate.questions.forEach((question) => usedQuestionIds.add(question.questionBankId))
                            )
                            remainingDifficulty[diff] = Math.max(0, diffRemaining - diffPickedCount)
                            objectiveRemaining -= diffPickedCount
                            pickedForObjective.push(...diffPicked)
                        }
                    }

                    if (objectiveRemaining > 0) {
                        const name = objectiveNameLookup.get(entry.objectiveId) || 'Seçilen öğrenme çıktısı'
                        Swal.fire(
                            'Uyarı',
                            `${name} için istenen zorluk dağılımında yeterli soru bulunamadı.`,
                            'warning'
                        )
                        return
                    }

                    pickedForObjective.forEach((candidate) => {
                        candidate.questions.forEach((question) => usedQuestionIds.add(question.questionBankId))
                    })
                    selected.push(...pickedForObjective)
                    totalSelected += entry.count
                    continue
                }

                const { selected: picked, totalSelected: pickedCount } = selectCandidatesForCount(eligible, entry.count)

                if (!picked.length || pickedCount < entry.count) {
                    const name = objectiveNameLookup.get(entry.objectiveId) || 'Seçilen öğrenme çıktısı'
                    Swal.fire(
                        'Uyarı',
                        `${name} için istenen sayıda soru seçilemedi. Lütfen sayıyı güncelleyiniz.`,
                        'warning'
                    )
                    return
                }

                picked.forEach((candidate) => {
                    candidate.questions.forEach((question) => usedQuestionIds.add(question.questionBankId))
                })

                const pickedKeys = new Set(picked.map((item) => item.key))
                remainingCandidates = remainingCandidates.filter((candidate) => !pickedKeys.has(candidate.key))
                selected.push(...picked)
                totalSelected += pickedCount
            }

            if (Object.keys(remainingDifficulty).length > 0) {
                const remainingTotal = Object.values(remainingDifficulty).reduce(
                    (sum, value) => sum + Math.max(0, Math.floor(Number(value) || 0)),
                    0
                )
                if (remainingTotal > 0) {
                    Swal.fire(
                        'Uyarı',
                        'Belirtilen zorluk dağılımı için yeterli soru bulunamadı. Lütfen adetleri güncelleyiniz.',
                        'warning'
                    )
                    return
                }
            }

            const payload = buildAutoAddPayload(selected)
            if (!payload) {
                Swal.fire('Uyarı', 'Eklenecek soru bulunamadı.', 'warning')
                return
            }

            const response = await addExamQuestions({ ...payload, autoCalculatePoints })
            await handleAutoAddResponse(response)
            Swal.fire('Başarılı', `${totalSelected} soru sınava eklendi.`, 'success')
            setIsAutoAddModalOpen(false)
        } catch (error: any) {
            console.error(error)
            const message = error?.response?.data || 'Sorular eklenemedi.'
            Swal.fire('Hata', message, 'error')
        } finally {
            setAutoAddSubmitting(false)
        }
    }, [
        autoAddOutcomeIncludeUsed,
        autoAddOutcomeRows,
        buildAutoAddPayload,
        buildAutoSelectionCandidates,
        canEditExamContent,
        examId,
        handleAutoAddResponse,
        selectCandidatesForCount,
        buildDifficultyAvailability,
        normalizedDifficultyRequests,
        semesterCourseId,
        autoCalculatePoints,
    ])

    const handleAutoAddSubmit = useCallback(async () => {
        if (autoAddTab === 'outcome') {
            await handleAutoAddByOutcome()
            return
        }
        await handleAutoAddByTag()
    }, [autoAddTab, handleAutoAddByOutcome, handleAutoAddByTag])

    const handleOpenAutoAddModal = useCallback(() => {
        if (!examId || !semesterCourseId) {
            Swal.fire('Uyarı', 'Soruları eklemek için önce sınavı oluşturmalısınız.', 'warning')
            return
        }

        if (!canEditExamContent) {
            Swal.fire('Uyarı', 'Sınav durumu nedeniyle düzenleme yapılamaz.', 'warning')
            return
        }

        setAutoAddTab('tag')
        setIsAutoAddModalOpen(true)
    }, [canEditExamContent, examId, semesterCourseId])

    const handleCloseAutoAddModal = useCallback(() => {
        if (autoAddSubmitting) {
            return
        }
        setIsAutoAddModalOpen(false)
        setAutoAddTagCount('1')
        setAutoAddIncludeUsed(false)
        setAutoAddOutcomeIncludeUsed(false)
        setAutoAddSelectedTagId(null)
        setAutoAddOutcomeRows([{ objectiveId: null, count: '' }])
        setAutoAddOutcomePool(null)
        setAutoAddDifficultyRows([{ difficulty: null, count: '' }])
    }, [autoAddSubmitting])

    const handleAddOutcomeRow = useCallback(() => {
        setAutoAddOutcomeRows((prev) => [...prev, { objectiveId: null, count: '' }])
    }, [])

    const handleOutcomeRowChange = useCallback((index: number, field: 'objectiveId' | 'count', value: string) => {
        setAutoAddOutcomeRows((prev) => {
            return prev.map((row, rowIndex) => {
                if (rowIndex !== index) {
                    return row
                }
                if (field === 'objectiveId') {
                    const parsed = Number(value)
                    return {
                        ...row,
                        objectiveId: Number.isFinite(parsed) ? parsed : null,
                    }
                }
                return {
                    ...row,
                    count: value,
                }
            })
        })
    }, [])

    const handleRemoveOutcomeRow = useCallback((index: number) => {
        setAutoAddOutcomeRows((prev) => {
            if (prev.length <= 1) {
                return [{ objectiveId: null, count: '' }]
            }
            return prev.filter((_, rowIndex) => rowIndex !== index)
        })
    }, [])

    const handleAddDifficultyRow = useCallback(() => {
        setAutoAddDifficultyRows((prev) => [...prev, { difficulty: null, count: '' }])
    }, [])

    const handleDifficultyRowChange = useCallback(
        (index: number, field: 'difficulty' | 'count', value: string) => {
            setAutoAddDifficultyRows((prev) =>
                prev.map((row, rowIndex) => {
                    if (rowIndex !== index) {
                        return row
                    }
                    if (field === 'difficulty') {
                        const parsed = Number(value)
                        return {
                            ...row,
                            difficulty: Number.isFinite(parsed) ? parsed : null,
                        }
                    }
                    return {
                        ...row,
                        count: value,
                    }
                })
            )
        },
        []
    )

    const handleRemoveDifficultyRow = useCallback((index: number) => {
        setAutoAddDifficultyRows((prev) => {
            if (prev.length <= 1) {
                return [{ difficulty: null, count: '' }]
            }
            return prev.filter((_, rowIndex) => rowIndex !== index)
        })
    }, [])

    const previewContent = useMemo(() => {
        if (!selectedQuestion) {
            return null
        }
        if (selectedQuestion.type === 'pool') {
            const q: ExamQuestionPoolQuestion = selectedQuestion.data
            const items = Array.isArray(q.questionItems) ? q.questionItems : []
            const hasItems = items.length > 0
            return (
                <div>
                    {q.sharedContentTitle && (
                        <div className='alert alert-primary py-2 mb-3'>
                            <div className='fw-semibold mb-1'>{q.sharedContentTitle}</div>
                            <div dangerouslySetInnerHTML={{ __html: enhanceHtmlContent(q.sharedContentContent) }}></div>
                        </div>
                    )}
                    <h5 className='fw-bold mb-3' dangerouslySetInnerHTML={{ __html: buildQuestionHtml(q) }} />
                    <div className='d-flex flex-wrap gap-2 align-items-center mb-3'>
                        {q.sharedContentId && <span className='badge badge-light-info'>Ortak Metinli</span>}
                        <span className={`badge ${hasItems ? 'badge-light-primary' : 'badge-light-warning'}`}>
                            {hasItems ? 'Çoktan Seçmeli' : 'Açık Uçlu'}
                        </span>
                    </div>
                    {hasItems ? (
                        <ol className='ps-4 manual-exam-question-options' type='A'>
                            {items.map((item) => (
                                <li key={item.id} className={item.isTrue ? 'fw-bold text-success mb-1' : 'mb-1'}>
                                    <span dangerouslySetInnerHTML={{ __html: buildOptionHtml(item) }}></span>
                                </li>
                            ))}
                        </ol>
                    ) : (
                        renderOpenEndedPlaceholder()
                    )}
                </div>
            )
        }
        const q: ExamQuestionAssignment = selectedQuestion.data
        const orderedOptions = getOrderedAssignmentOptions(q)
        const hasOptions = orderedOptions.length > 0
        return (
            <div>
                {q.sharedContentTitle && (
                    <div className='alert alert-primary py-2 mb-3'>
                        <div className='fw-semibold mb-1'>{q.sharedContentTitle}</div>
                        <div dangerouslySetInnerHTML={{ __html: enhanceHtmlContent(q.sharedContentContent) }}></div>
                    </div>
                )}
                <h5 className='fw-bold mb-3' dangerouslySetInnerHTML={{ __html: buildQuestionHtml(q) }} />
                <div className='d-flex flex-wrap gap-2 align-items-center mb-3'>
                    {q.sharedContentId && <span className='badge badge-light-info'>Ortak Metinli</span>}
                    <span className={`badge ${hasOptions ? 'badge-light-primary' : 'badge-light-warning'}`}>
                        {hasOptions ? 'Çoktan Seçmeli' : 'Açık Uçlu'}
                    </span>
                </div>
                {hasOptions ? (
                    <ol className='ps-4 manual-exam-question-options' type='A'>
                        {orderedOptions.map((option) => (
                            <li key={option.id} className={option.isTrue ? 'fw-bold text-success mb-1' : 'mb-1'}>
                                <span dangerouslySetInnerHTML={{ __html: buildOptionHtml(option) }}></span>
                            </li>
                        ))}
                    </ol>
                ) : (
                    renderOpenEndedPlaceholder()
                )}
            </div>
        )
    }, [selectedQuestion])

    const questionSummary = useMemo(() => {
        const difficultyCounts = new Map<string, { count: number; value: number | null }>()
        const outcomeCounts = new Map<string, number>()
        let total = 0
        const defaultOutcomeLabel = isBoardCourse ? 'Belirtilmemiş Birim' : 'Belirtilmemiş Çıktı'

        const getDifficultyInfo = (question: ExamQuestionAssignment): { label: string; value: number | null } => {
            const raw = question as any
            const questionBankId = Number(question.questionBankId)
            const metadata = Number.isFinite(questionBankId) ? questionMetadataMap[questionBankId] : undefined
            const label = pickFirstString(
                raw?.difficultyName,
                raw?.DifficultyName,
                raw?.difficultyLevelName,
                raw?.DifficultyLevelName,
                typeof raw?.difficulty === 'string' ? raw.difficulty : undefined,
                typeof raw?.Difficulty === 'string' ? raw.Difficulty : undefined,
                metadata?.difficultyLabel ?? undefined
            )
            const numeric = pickFirstNumber(
                raw?.difficultyLevel,
                raw?.DifficultyLevel,
                typeof raw?.difficulty === 'number' ? raw.difficulty : undefined,
                typeof raw?.Difficulty === 'number' ? raw.Difficulty : undefined,
                typeof metadata?.difficultyValue === 'number' ? metadata.difficultyValue : undefined
            )
            const normalized = normalizeDifficultyDetails(label, numeric)
            if (normalized.label) {
                return { label: normalized.label, value: typeof normalized.value === 'number' ? normalized.value : null }
            }
            return { label: 'Belirtilmemiş', value: typeof normalized.value === 'number' ? normalized.value : null }
        }

        const getOutcomeLabel = (question: ExamQuestionAssignment, groupName?: string | null): string => {
            const raw = question as any
            const questionBankId = Number(question.questionBankId)
            const metadata = Number.isFinite(questionBankId) ? questionMetadataMap[questionBankId] : undefined
            const label = pickFirstString(
                question.learningObjectiveName,
                raw?.LearningObjectiveName,
                metadata?.learningObjectiveName,
                groupName
            )
            return label || defaultOutcomeLabel
        }

        groupState.forEach((group) => {
            group.questions.forEach((question) => {
                total += 1
                const { label: difficultyLabel, value: difficultyValue } = getDifficultyInfo(question)
                const existingDifficulty = difficultyCounts.get(difficultyLabel)
                if (existingDifficulty) {
                    existingDifficulty.count += 1
                    if (existingDifficulty.value === null && typeof difficultyValue === 'number') {
                        existingDifficulty.value = difficultyValue
                    } else if (
                        typeof existingDifficulty.value === 'number' &&
                        typeof difficultyValue === 'number' &&
                        difficultyValue < existingDifficulty.value
                    ) {
                        existingDifficulty.value = difficultyValue
                    }
                } else {
                    difficultyCounts.set(difficultyLabel, { count: 1, value: typeof difficultyValue === 'number' ? difficultyValue : null })
                }

                const outcomeLabel = getOutcomeLabel(question, group.groupName)
                outcomeCounts.set(outcomeLabel, (outcomeCounts.get(outcomeLabel) || 0) + 1)
            })
        })

        const difficulty = Array.from(difficultyCounts.entries())
            .map(([label, data]) => ({ label, count: data.count, value: data.value }))
            .sort((a, b) => {
                const aValue = typeof a.value === 'number' ? a.value : Number.POSITIVE_INFINITY
                const bValue = typeof b.value === 'number' ? b.value : Number.POSITIVE_INFINITY
                if (aValue !== bValue) {
                    return aValue - bValue
                }
                return a.label.localeCompare(b.label)
            })
            .map(({ label, count }) => ({ label, count }))
        const outcomes = Array.from(outcomeCounts.entries())
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
            .map(([label, count]) => ({ label, count }))

        return { total, difficulty, outcomes }
    }, [groupState, isBoardCourse, questionMetadataMap])

    const renderQuestionPool = () => {
        const filterToolbar = (
            <div className='d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3'>
                <div className='d-flex flex-wrap align-items-center gap-2'>
                    <span className='fw-semibold text-muted'>Etiketler:</span>
                    {isTagListLoading ? (
                        <span className='text-muted small'>Yükleniyor...</span>
                    ) : normalizedTagOptions.length > 0 ? (
                        normalizedTagOptions.map((tag) => {
                            const isSelected = selectedTagIdSet.has(tag.id)
                            return (
                                <button
                                    key={tag.id}
                                    type='button'
                                    className={`btn btn-sm ${isSelected ? 'btn-primary' : 'btn-light-primary'}`}
                                    onClick={() => handleToggleTagFilter(tag.id)}
                                >
                                    #{tag.name}
                                    {typeof tag.questionCount === 'number' && tag.questionCount > 0 && (
                                        <span className='badge bg-white text-primary ms-2'>{tag.questionCount}</span>
                                    )}
                                </button>
                            )
                        })
                    ) : (
                        <span className='text-muted small'>Tanımlı etiket bulunamadı.</span>
                    )}
                    {selectedTagIds.length > 0 && (
                        <button
                            type='button'
                            className='btn btn-link btn-sm text-decoration-none text-muted'
                            onClick={handleClearTagFilters}
                        >
                            Temizle
                        </button>
                    )}
                </div>
                <div className='form-check form-switch m-0 ms-lg-auto'>
                    <input
                        className='form-check-input'
                        type='checkbox'
                        role='switch'
                        id='toggle-used-questions'
                        checked={showPreviouslyUsedQuestions}
                        onChange={(event) => setShowPreviouslyUsedQuestions(event.target.checked)}
                        disabled={loading}
                    />
                    <label className='form-check-label ms-2' htmlFor='toggle-used-questions'>
                        Daha önce kullanılan soruları göster
                    </label>
                </div>
            </div>
        )

        if (!questionPool || !questionPool.learningObjectives.length) {
            return (
                <div className='question-pool-list'>
                    {filterToolbar}
                    <div className='alert alert-light border mb-0'>
                        Seçilen {isBoardCourse ? 'birimde' : 'ders öğrenme çıktısında'} soru bulunamadı.
                    </div>
                </div>
            )
        }

        const processedQuestionIds = new Set<number>()

        const objectiveElements = questionPool.learningObjectives
            .map((objective: ExamQuestionPoolLearningObjective) => {
                const groups: Array<{
                    key: string
                    baseQuestion: ExamQuestionPoolQuestion
                    questions: ExamQuestionPoolQuestion[]
                    isGrouped: boolean
                }> = []

                objective.questions.forEach((question) => {
                    if (processedQuestionIds.has(question.questionBankId)) {
                        return
                    }
                    const related = collectSharedGroupQuestions(question)
                    related.forEach((item) => processedQuestionIds.add(item.questionBankId))
                    groups.push({
                        key: question.sharedContentId ? `shared-${question.sharedContentId}` : `single-${question.questionBankId}`,
                        baseQuestion: question,
                        questions: related,
                        isGrouped: related.length > 1 && !!question.sharedContentId,
                    })
                })

                if (!groups.length) {
                    return null
                }

                const shouldDisplayQuestion = (item: ExamQuestionPoolQuestion) => {
                    const usageCount = typeof item.usageCount === 'number' ? item.usageCount : Number(item.usageCount || 0)
                    const isPreviouslyUsed = usageCount > 0
                    if (existingQuestionIds.has(item.questionBankId)) {
                        return true
                    }
                    if (!showPreviouslyUsedQuestions && isPreviouslyUsed) {
                        return false
                    }
                    return true
                }

                const visibleGroupData = groups
                    .map(({ key, baseQuestion, questions, isGrouped }) => {
                        const visibleQuestions = questions.filter(shouldDisplayQuestion)
                        if (!visibleQuestions.length) {
                            return null
                        }

                        const addedCount = questions.filter((q) => existingQuestionIds.has(q.questionBankId)).length
                        const fullyAdded = addedCount === questions.length
                        const partiallyAdded = addedCount > 0 && !fullyAdded
                        const addSourceQuestion = visibleQuestions[0] ?? baseQuestion

                        return {
                            key,
                            baseQuestion,
                            questions,
                            visibleQuestions,
                            isGrouped,
                            fullyAdded,
                            partiallyAdded,
                            addSourceQuestion,
                        }
                    })
                    .filter(
                        (item):
                            item is {
                                key: string
                                baseQuestion: ExamQuestionPoolQuestion
                                questions: ExamQuestionPoolQuestion[]
                                visibleQuestions: ExamQuestionPoolQuestion[]
                                isGrouped: boolean
                                fullyAdded: boolean
                                partiallyAdded: boolean
                                addSourceQuestion: ExamQuestionPoolQuestion
                            } => Boolean(item)
                    )

                const visibleQuestionCount = visibleGroupData.reduce(
                    (sum, group) => sum + group.visibleQuestions.length,
                    0
                )

                if (!visibleQuestionCount) {
                    return null
                }

                const visibleGroups = visibleGroupData.map(
                    ({
                        key,
                        baseQuestion,
                        questions,
                        visibleQuestions,
                        isGrouped,
                        fullyAdded,
                        partiallyAdded,
                        addSourceQuestion,
                    }) => (
                        <div
                            key={key}
                            className={`border rounded p-3 manual-exam-pool-item ${fullyAdded ? 'bg-light' : 'bg-white'}`}
                        >
                            {baseQuestion.sharedContentTitle && (
                                <div className='alert alert-primary py-2 mb-3'>
                                    <div className='fw-semibold mb-1'>{baseQuestion.sharedContentTitle}</div>
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: enhanceHtmlContent(baseQuestion.sharedContentContent),
                                        }}
                                    ></div>
                                </div>
                            )}
                            <div className='d-flex flex-column gap-3'>
                                {visibleQuestions.map((item, index) => {
                                    const usageCount =
                                        typeof item.usageCount === 'number' ? item.usageCount : Number(item.usageCount || 0)
                                    const lastUsedLabel = (() => {
                                        if (!item.lastUsedAt) {
                                            return null
                                        }
                                        const parsed = new Date(item.lastUsedAt)
                                        if (Number.isNaN(parsed.getTime())) {
                                            return null
                                        }
                                        return parsed.toLocaleDateString('tr-TR')
                                    })()

                                    return (
                                        <div
                                            key={item.questionBankId}
                                            className='d-flex justify-content-between align-items-start gap-2 manual-exam-pool-item-row'
                                        >
                                            <div className='flex-grow-1'>
                                                {isGrouped && (
                                                    <div className='text-muted small fw-semibold mb-1'>{index + 1}. Soru</div>
                                                )}
                                            <div
                                                className='fw-semibold flex-grow-1'
                                                dangerouslySetInnerHTML={{
                                                    __html: buildQuestionHtml(item),
                                                }}
                                            ></div>
                                                {usageCount > 0 && (
                                                    <div className='text-muted small mt-1'>
                                                        Daha önce {usageCount} kez kullanıldı
                                                        {lastUsedLabel ? ` (Son: ${lastUsedLabel})` : ''}
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                className='btn btn-link p-0 manual-exam-preview-trigger'
                                                type='button'
                                                onClick={() => {
                                                    setSelectedQuestion({ type: 'pool', data: item })
                                                    if (isCompactViewport) {
                                                        setIsPreviewModalOpen(true)
                                                    }
                                                }}
                                            >
                                                Önizle
                                            </button>
                                        </div>
                                    )
                                })}
                            </div>
                            <div className='d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3'>
                                <div className='text-muted small'>
                                    {isGrouped ? `${questions.length} soruluk grup` : 'Tek soru'}
                                    {partiallyAdded && ' (bazıları eklendi)'}
                                </div>
                                <button
                                    type='button'
                                    className='btn btn-sm btn-light-primary'
                                    disabled={fullyAdded || submitting || !canEditExamContent}
                                    onClick={() => handleAddQuestion(addSourceQuestion)}
                                >
                                    {fullyAdded ? (isGrouped ? 'Grup Eklendi' : 'Eklendi') : isGrouped ? 'Grubu Ekle' : 'Ekle'}
                                </button>
                            </div>
                        </div>
                    )
                )

                return (
                    <div key={objective.id} className='mb-5'>
                        <div className='d-flex align-items-center justify-content-between mb-2'>
                            <h5 className='fw-bold mb-0'>{objective.name}</h5>
                            <span className='badge badge-light-primary'>{visibleQuestionCount} soru</span>
                        </div>
                        <div className='border rounded p-3'>
                            <div className='d-flex flex-column gap-3' style={{ maxHeight: '640px', overflowY: 'auto' }}>
                                {visibleGroups}
                            </div>
                        </div>
                    </div>
                )
            })
            .filter((element): element is React.ReactElement => Boolean(element))

        if (!objectiveElements.length) {
            return (
                <div className='question-pool-list'>
                    {filterToolbar}
                    <div className='alert alert-light border mb-0'>
                        Filtre nedeniyle görüntülenecek soru bulunamadı.
                    </div>
                </div>
            )
        }

        return (
            <div className='question-pool-list'>
                {filterToolbar}
                {objectiveElements}
            </div>
        )
    }

    const renderAssignments = () => {
        if (!groupState.length) {
            return <div className='alert alert-light border'>Henüz soru eklenmemiş.</div>
        }
        return (
            <div className='exam-assignment-list'>
                <div className='d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3'>
                    <div>
                        <div className='fw-semibold'>
                            Toplam Puan:{' '}
                            <span className={isTotalValid ? 'text-success' : 'text-danger'}>
                                {totalPoint.toFixed(2)}
                            </span>
                        </div>
                        {!isTotalValid && (
                            <div className='text-danger small'>Puan toplamı 100 olmalıdır.</div>
                        )}
                        {invalidPointIds.size > 0 && (
                            <div className='text-danger small'>Geçersiz puan değerleri mevcut.</div>
                        )}
                    </div>
                    <div className='form-check form-check-custom form-check-solid'>
                        <input
                            className='form-check-input'
                            type='checkbox'
                            id='manual-exam-auto-calculate-points'
                            checked={autoCalculatePoints}
                            onChange={(event) => setAutoCalculatePoints(event.target.checked)}
                            disabled={!canEditExamContent}
                        />
                        <label className='form-check-label ms-2 fw-semibold' htmlFor='manual-exam-auto-calculate-points'>
                            Puanları otomatik hesapla
                        </label>
                    </div>
                    <button
                        type='button'
                        className='btn btn-primary'
                        onClick={handleSavePoints}
                        disabled={
                            pointSaving || invalidPointIds.size > 0 || !isTotalValid || !canEditExamContent
                        }
                    >
                        {pointSaving ? 'Kaydediliyor...' : 'Puanları Kaydet'}
                    </button>
                </div>
                {groupState.map((group, groupIndex) => {
                    const groupWrapperClasses = [
                        'manual-exam-group',
                        'mb-5',
                        groupIndex % 2 === 0 ? 'manual-exam-group--even' : 'manual-exam-group--odd',
                    ]

                    let lastSharedContentId: number | null = null

                    return (
                        <div key={`${group.groupType}-${group.groupId}`} className={groupWrapperClasses.join(' ')}>
                            <div className='d-flex justify-content-between align-items-center mb-2 manual-exam-group__header'>
                                <h5 className='fw-bold mb-0'>{group.groupName || 'Grup'}</h5>
                                <span className='badge badge-light-primary'>{group.questions.length} soru</span>
                            </div>
                            <div className='manual-exam-group__content border rounded p-3'>
                                <div className='d-flex flex-column gap-3' style={{ maxHeight: '640px', overflowY: 'auto' }}>
                                    {group.questions.map((question, questionIndex) => {
                                        const sharedContentId = Number(question.sharedContentId)
                                        const hasSharedContent = Number.isFinite(sharedContentId) && sharedContentId > 0
                                        const isFirstSharedContentQuestion =
                                            hasSharedContent && sharedContentId !== lastSharedContentId
                                        if (hasSharedContent) {
                                            lastSharedContentId = sharedContentId
                                        } else {
                                            lastSharedContentId = null
                                        }

                                        const orderedOptions = getOrderedAssignmentOptions(question)
                                        const hasOptions = orderedOptions.length > 0
                                        const sharedContentShuffleValue = hasSharedContent
                                            ? getSharedContentShuffleValue(sharedContentId)
                                            : null
                                        const showSharedContentBlock =
                                            question.sharedContentTitle && (isFirstSharedContentQuestion || !hasSharedContent)

                                        return (
                                            <div key={question.examQuestionId} className='border rounded p-3 bg-white'>
                                                {showSharedContentBlock && (
                                                    <div className='d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3'>
                                                        <div className='alert alert-primary py-2 mb-0 flex-grow-1'>
                                                            <div className='fw-semibold mb-1'>{question.sharedContentTitle}</div>
                                                            <div
                                                                dangerouslySetInnerHTML={{
                                                                    __html: enhanceHtmlContent(question.sharedContentContent),
                                                                }}
                                                            ></div>
                                                        </div>
                                                        {hasSharedContent && (
                                                            <div className='form-check form-switch form-check-custom form-check-solid ms-auto'>
                                                                <input
                                                                    className='form-check-input'
                                                                    type='checkbox'
                                                                    id={`shared-content-shuffle-${sharedContentId}`}
                                                                    checked={!!sharedContentShuffleValue}
                                                                    disabled={bookletLoading || !canAdjustBookletSettings}
                                                                    onChange={() => {
                                                                        void handleToggleSharedContentShuffle(sharedContentId)
                                                                    }}
                                                                />
                                                                <label
                                                                    className='form-check-label fw-semibold'
                                                                    htmlFor={`shared-content-shuffle-${sharedContentId}`}
                                                                >
                                                                    Bu madde kümesindeki sorular kitapçıklarda karışsın
                                                                </label>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                <div className='d-flex justify-content-between align-items-start mb-2 manual-exam-assignment-row'>
                                                    <div>
                                                        <div className='fw-semibold d-flex align-items-start gap-2 flex-nowrap manual-exam-question-header'>
                                                            <span className='manual-exam-question-number d-inline-flex align-items-start flex-shrink-0 text-nowrap'>
                                                                {typeof question.order === 'number' && Number.isFinite(question.order)
                                                                    ? question.order
                                                                    : questionIndex + 1}
                                                                .
                                                            </span>
                                                        <div className='d-flex align-items-start gap-2 flex-grow-1'>
                                                            <div
                                                                className='flex-grow-1 manual-exam-question-content'
                                                                dangerouslySetInnerHTML={{
                                                                    __html: buildQuestionHtml(question),
                                                                }}
                                                            ></div>
                                                            </div>
                                                        </div>
                                                        <div className='mt-2'>
                                                            <label
                                                                htmlFor={`point-${question.examQuestionId}`}
                                                                className='form-label fw-semibold small text-muted mb-1'
                                                            >
                                                                Puan
                                                            </label>
                                                            <input
                                                                id={`point-${question.examQuestionId}`}
                                                                type='text'
                                                                className={`form-control form-control-sm ${invalidPointIds.has(question.examQuestionId) ? 'is-invalid' : ''
                                                                    }`}
                                                                value={pointInputs[question.examQuestionId] ?? ''}
                                                                onChange={(event) =>
                                                                    handlePointInputChange(question.examQuestionId, event.target.value)
                                                                }
                                                                onBlur={() => handlePointInputBlur(question.examQuestionId)}
                                                                placeholder='0,00'
                                                                disabled={!canEditExamContent}
                                                            />
                                                            {invalidPointIds.has(question.examQuestionId) && (
                                                                <div className='invalid-feedback'>Geçerli bir sayı giriniz.</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className='d-flex gap-2'>
                                                        <button
                                                            className='btn btn-sm btn-icon btn-light'
                                                            onClick={() => handleMoveQuestion(groupIndex, questionIndex, 'up')}
                                                            disabled={questionIndex === 0 || !canEditExamContent}
                                                            title='Yukarı'
                                                        >
                                                            ↑
                                                        </button>
                                                        <button
                                                            className='btn btn-sm btn-icon btn-light'
                                                            onClick={() => handleMoveQuestion(groupIndex, questionIndex, 'down')}
                                                            disabled={
                                                                questionIndex === group.questions.length - 1 || !canEditExamContent
                                                            }
                                                            title='Aşağı'
                                                        >
                                                            ↓
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className='d-flex flex-wrap gap-2 align-items-center mt-2 mb-3'>
                                                    {question.sharedContentId && (
                                                        <span className='badge badge-light-info'>Ortak Metinli</span>
                                                    )}
                                                    <span className={`badge ${hasOptions ? 'badge-light-primary' : 'badge-light-warning'}`}>
                                                        {hasOptions ? 'Çoktan Seçmeli' : 'Açık Uçlu'}
                                                    </span>
                                                </div>
                                                {hasOptions ? (
                                                    <ol className='ps-4 manual-exam-question-options mb-3' type='A'>
                                                        {orderedOptions.map((option) => (
                                                            <li
                                                                key={option.id}
                                                                className={option.isTrue ? 'fw-semibold text-success mb-2' : 'mb-2'}
                                                            >
                                                                <span
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: buildOptionHtml(option),
                                                                    }}
                                                                ></span>
                                                            </li>
                                                        ))}
                                                    </ol>
                                                ) : (
                                                    renderOpenEndedPlaceholder('mb-3')
                                                )}
                                                <div className='d-flex flex-wrap gap-2 align-items-center mb-2'>
                                                    <button
                                                        type='button'
                                                        className='btn btn-sm btn-light-danger'
                                                        disabled={!canEditExamContent}
                                                        onClick={() => handleRemoveQuestion(question)}
                                                    >
                                                        Kaldır
                                                    </button>
                                                </div>
                                                <button className='btn btn-link p-0 manual-exam-preview-trigger'
                                                    type='button'
                                                    onClick={() => {
                                                        setSelectedQuestion({ type: 'assignment', data: question })
                                                        if (isCompactViewport) {
                                                            setIsPreviewModalOpen(true)
                                                        }
                                                    }}
                                                >
                                                    Önizle
                                                </button>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
        )
    }

    if (showInitialLoader) {
        return (
            <>
                <Modal
                    show={isSendToMeasurementCenterModalOpen}
                    onHide={handleCloseSendToMeasurementCenterModal}
                    centered
                    backdrop={statusUpdating ? 'static' : true}
                    keyboard={!statusUpdating}
                >
                    <Modal.Header closeButton={!statusUpdating}>
                        <Modal.Title>Ölçme Merkezine Gönder</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className='alert alert-warning text-start'>
                            <p className='mb-2'>Sınav kitapçığı ve soruların son kontrolünü gerçekleştirdim.</p>
                            <p className='mb-2'>“Merkeze Gönder” işlemi ile birlikte sınav baskıya alınacaktır.</p>
                            <p className='mb-2'>Bu onayı vererek, baskıya gönderilen içeriklerdeki olası hata veya eksikliklerden sorumluluğun tarafıma ait olduğunu kabul ederim.</p>
                            <p className='mb-0'>Sınav baskı sürecinde Sınav Hizmetleri Birimi yalnızca teknik çoğaltma işlemini yürütmektedir.</p>
                        </div>
                        <p className='fw-semibold text-start mt-4 mb-3'>Onaylıyor musunuz?</p>
                        <div>
                            <label className='form-label fw-semibold'>Ölçme Merkezi Notu</label>
                            <textarea
                                className='form-control'
                                rows={4}
                                value={sendToMeasurementCenterNote}
                                onChange={(event) => setSendToMeasurementCenterNote(event.target.value)}
                                placeholder='Notunuzu yazınız'
                                disabled={statusUpdating}
                            />
                            <div className='form-text mt-2'>
                                Aşağıya sınavla ilgili notları yazabilirsiniz. Özellikle ortak yapılacak derslerin her birisi için ders kodu yazmayı unutmayınız.
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button
                            type='button'
                            className='btn btn-light'
                            onClick={handleCloseSendToMeasurementCenterModal}
                            disabled={statusUpdating}
                        >
                            Vazgeç
                        </button>
                        <button
                            type='button'
                            className='btn btn-primary'
                            onClick={handleConfirmSendToMeasurementCenter}
                            disabled={statusUpdating}
                        >
                            {statusUpdating ? 'Gönderiliyor...' : 'Ölçme Merkezine Gönder'}
                        </button>
                    </Modal.Footer>
                </Modal>
                <div className='container py-10 d-flex justify-content-center align-items-center'>
                    <div className='text-center d-flex flex-column align-items-center gap-3'>
                        <span className='spinner-border text-primary' role='status' aria-hidden='true' />
                        <span className='fw-semibold text-muted'>Sınav bilgileri yükleniyor...</span>
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            <Modal
                show={isAutoAddModalOpen}
                onHide={handleCloseAutoAddModal}
                centered
                size='lg'
                backdrop={autoAddSubmitting ? 'static' : true}
                keyboard={!autoAddSubmitting}
            >
                <Modal.Header closeButton={!autoAddSubmitting}>
                    <Modal.Title>Otomatik Soru Ekle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ul className='nav nav-tabs nav-line-tabs nav-bold mb-4'>
                        <li className='nav-item'>
                            <button
                                type='button'
                                className={`nav-link ${autoAddTab === 'tag' ? 'active' : ''}`}
                                onClick={() => setAutoAddTab('tag')}
                            >
                                Etikete Göre
                            </button>
                        </li>
                        <li className='nav-item'>
                            <button
                                type='button'
                                className={`nav-link ${autoAddTab === 'outcome' ? 'active' : ''}`}
                                onClick={() => setAutoAddTab('outcome')}
                            >
                                Öğrenme Çıktısına Göre
                            </button>
                        </li>
                    </ul>
                    {autoAddTab === 'tag' ? (
                        <div className='d-flex flex-column gap-4'>
                            <div>
                                <label className='form-label fw-semibold'>Etiket</label>
                                {normalizedTagOptions.length > 0 ? (
                                    <select
                                        className='form-select'
                                        value={autoAddSelectedTagId ?? ''}
                                        disabled={autoAddSubmitting}
                                        onChange={(event) => setAutoAddSelectedTagId(Number(event.target.value) || null)}
                                    >
                                        {normalizedTagOptions.map((tag) => (
                                            <option key={tag.id} value={tag.id}>
                                                #{tag.name}
                                                {typeof tag.questionCount === 'number' && tag.questionCount > 0
                                                    ? ` (${tag.questionCount} soru)`
                                                    : ''}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <div className='alert alert-light border mb-0'>
                                        Tanımlı etiket bulunamadı. Soru eklemek için önce etiket oluşturmalısınız.
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className='form-label fw-semibold'>Eklenecek Soru Sayısı</label>
                                <input
                                    type='number'
                                    min={1}
                                    className='form-control'
                                    value={autoAddTagCount}
                                    onChange={(event) => setAutoAddTagCount(event.target.value)}
                                    disabled={autoAddSubmitting}
                                />
                            </div>
                            <div className='form-check form-switch form-check-custom form-check-solid'>
                                <input
                                    className='form-check-input'
                                    type='checkbox'
                                    checked={autoAddIncludeUsed}
                                    disabled={autoAddSubmitting}
                                    id='auto-add-tag-include-used'
                                    onChange={(event) => setAutoAddIncludeUsed(event.target.checked)}
                                />
                                <label className='form-check-label fw-semibold' htmlFor='auto-add-tag-include-used'>
                                    Önceden kullanılan soruları da dahil et
                                </label>
                            </div>
                            <div className='border rounded-3 p-3 bg-light'>
                                <div className='d-flex align-items-center justify-content-between mb-3'>
                                    <div>
                                        <div className='fw-semibold'>Zorluk Dağılımı (Opsiyonel)</div>
                                        <div className='text-muted small'>
                                            Belirtilen zorluklarda yeterli soru yoksa ekleme yapılmaz.
                                            <br />
                                            0-1 arası madde istatistiği olan sorular için: 0-0,20 → Seviye 5, 0,20-0,40 → 4, 0,40-0,60 → 3, 0,60-0,80 → 2, 0,80-1 → 1.
                                        </div>
                                    </div>
                                    <button
                                        type='button'
                                        className='btn btn-sm btn-light-primary'
                                        onClick={handleAddDifficultyRow}
                                        disabled={autoAddSubmitting}
                                    >
                                        + Satır Ekle
                                    </button>
                                </div>
                                <div className='d-flex flex-column gap-3'>
                                    {autoAddDifficultyRows.map((row, index) => (
                                        <div
                                            key={`auto-add-difficulty-row-${index}`}
                                            className='d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-3'
                                        >
                                            <div className='flex-grow-1 w-100'>
                                                <label className='form-label fw-semibold'>Zorluk Düzeyi</label>
                                                <select
                                                    className='form-select'
                                                    value={row.difficulty ?? ''}
                                                    onChange={(event) =>
                                                        handleDifficultyRowChange(
                                                            index,
                                                            'difficulty',
                                                            event.target.value
                                                        )
                                                    }
                                                    disabled={autoAddSubmitting}
                                                >
                                                    <option value=''>Seçiniz</option>
                                                    {DIFFICULTY_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className='w-100' style={{ maxWidth: '180px' }}>
                                                <label className='form-label fw-semibold'>Soru Adedi</label>
                                                <input
                                                    type='number'
                                                    min={1}
                                                    className='form-control'
                                                    value={row.count}
                                                    onChange={(event) =>
                                                        handleDifficultyRowChange(index, 'count', event.target.value)
                                                    }
                                                    disabled={autoAddSubmitting}
                                                />
                                            </div>
                                            <button
                                                type='button'
                                                className='btn btn-icon btn-light-danger mt-2 mt-lg-4'
                                                onClick={() => handleRemoveDifficultyRow(index)}
                                                disabled={autoAddSubmitting || autoAddDifficultyRows.length <= 1}
                                                title='Satırı Kaldır'
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className='alert alert-info mb-0'>
                                <div className='fw-semibold mb-1'>Bilgi</div>
                                <div>
                                    Seçilen etikete ait sorular rasgele belirlenir. Daha önce eklenen sorular otomatik olarak hariç tutulur.
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className='d-flex flex-column gap-4'>
                            <div className='form-check form-switch form-check-custom form-check-solid'>
                                <input
                                    className='form-check-input'
                                    type='checkbox'
                                    checked={autoAddOutcomeIncludeUsed}
                                    disabled={autoAddSubmitting}
                                    id='auto-add-outcome-include-used'
                                    onChange={(event) => setAutoAddOutcomeIncludeUsed(event.target.checked)}
                                />
                                <label
                                    className='form-check-label fw-semibold'
                                    htmlFor='auto-add-outcome-include-used'
                                >
                                    Önceden kullanılan soruları da dahil et
                                </label>
                            </div>
                            <div className='border rounded-3 p-3 bg-light'>
                                <div className='d-flex align-items-center justify-content-between mb-3'>
                                    <div>
                                        <div className='fw-semibold'>Zorluk Dağılımı (Opsiyonel)</div>
                                        <div className='text-muted small'>
                                            Seçilen zorlukta yeterli soru yoksa ekleme yapılmaz.
                                            <br />
                                            0-1 arası madde istatistiği olan sorular için: 0-0,20 → Seviye 5, 0,20-0,40 → 4, 0,40-0,60 → 3, 0,60-0,80 → 2, 0,80-1 → 1.
                                        </div>
                                    </div>
                                    <button
                                        type='button'
                                        className='btn btn-sm btn-light-primary'
                                        onClick={handleAddDifficultyRow}
                                        disabled={autoAddSubmitting}
                                    >
                                        + Satır Ekle
                                    </button>
                                </div>
                                <div className='d-flex flex-column gap-3'>
                                    {autoAddDifficultyRows.map((row, index) => (
                                        <div
                                            key={`auto-add-difficulty-outcome-row-${index}`}
                                            className='d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-3'
                                        >
                                            <div className='flex-grow-1 w-100'>
                                                <label className='form-label fw-semibold'>Zorluk Düzeyi</label>
                                                <select
                                                    className='form-select'
                                                    value={row.difficulty ?? ''}
                                                    onChange={(event) =>
                                                        handleDifficultyRowChange(
                                                            index,
                                                            'difficulty',
                                                            event.target.value
                                                        )
                                                    }
                                                    disabled={autoAddSubmitting}
                                                >
                                                    <option value=''>Seçiniz</option>
                                                    {DIFFICULTY_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className='w-100' style={{ maxWidth: '180px' }}>
                                                <label className='form-label fw-semibold'>Soru Adedi</label>
                                                <input
                                                    type='number'
                                                    min={1}
                                                    className='form-control'
                                                    value={row.count}
                                                    onChange={(event) =>
                                                        handleDifficultyRowChange(index, 'count', event.target.value)
                                                    }
                                                    disabled={autoAddSubmitting}
                                                />
                                            </div>
                                            <button
                                                type='button'
                                                className='btn btn-icon btn-light-danger mt-2 mt-lg-4'
                                                onClick={() => handleRemoveDifficultyRow(index)}
                                                disabled={autoAddSubmitting || autoAddDifficultyRows.length <= 1}
                                                title='Satırı Kaldır'
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {autoAddLoading ? (
                                <div className='d-flex align-items-center gap-3'>
                                    <span className='spinner-border text-primary' role='status' aria-hidden='true' />
                                    <span className='fw-semibold text-muted'>Öğrenme çıktıları yükleniyor...</span>
                                </div>
                            ) : autoAddOutcomeOptions.length === 0 ? (
                                <div className='alert alert-light border mb-0'>
                                    Uygun öğrenme çıktısı bulunamadı veya soru havuzu boş.
                                </div>
                            ) : (
                                <div className='d-flex flex-column gap-3'>
                                    {autoAddOutcomeRows.map((row, index) => (
                                        <div
                                            key={`auto-add-outcome-row-${index}`}
                                            className='d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-3'
                                        >
                                            <div className='flex-grow-1 w-100'>
                                                <label className='form-label fw-semibold'>Öğrenme Çıktısı</label>
                                                <select
                                                    className='form-select'
                                                    value={row.objectiveId ?? ''}
                                                    onChange={(event) =>
                                                        handleOutcomeRowChange(index, 'objectiveId', event.target.value)
                                                    }
                                                    disabled={autoAddSubmitting}
                                                >
                                                    <option value=''>Öğrenme çıktısı seçiniz</option>
                                                    {autoAddOutcomeOptions.map((option) => (
                                                        <option key={option.id} value={option.id}>
                                                            {option.name} ({option.count} uygun soru)
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className='w-100' style={{ maxWidth: '180px' }}>
                                                <label className='form-label fw-semibold'>Soru Adedi</label>
                                                <input
                                                    type='number'
                                                    min={1}
                                                    className='form-control'
                                                    value={row.count}
                                                    onChange={(event) =>
                                                        handleOutcomeRowChange(index, 'count', event.target.value)
                                                    }
                                                    disabled={autoAddSubmitting}
                                                />
                                            </div>
                                            <button
                                                type='button'
                                                className='btn btn-icon btn-light-danger mt-2 mt-lg-4'
                                                onClick={() => handleRemoveOutcomeRow(index)}
                                                disabled={autoAddSubmitting || autoAddOutcomeRows.length <= 1}
                                                title='Satırı Kaldır'
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                    <div>
                                        <button
                                            type='button'
                                            className='btn btn-light-primary btn-sm'
                                            onClick={handleAddOutcomeRow}
                                            disabled={autoAddSubmitting}
                                        >
                                            + Satır Ekle
                                        </button>
                                    </div>
                                    <div className='alert alert-info mb-0'>
                                        <div className='fw-semibold mb-1'>Bilgi</div>
                                        <div>
                                            Her öğrenme çıktısı için belirttiğiniz sayıda soru rasgele seçilir. Mevcut sorular ve ortak metin zorunlulukları dikkate alınır.
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <div className='text-muted small me-auto'>
                        Seçim yapılırken sınavda bulunan sorular ve grup kuralları göz önünde bulundurulur.
                    </div>
                    <button
                        type='button'
                        className='btn btn-light'
                        onClick={handleCloseAutoAddModal}
                        disabled={autoAddSubmitting}
                    >
                        Vazgeç
                    </button>
                    <button
                        type='button'
                        className='btn btn-primary'
                        onClick={handleAutoAddSubmit}
                        disabled={autoAddSubmitting || autoAddLoading}
                    >
                        {autoAddSubmitting ? 'Ekleme yapılıyor...' : 'Soruları Ekle'}
                    </button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={isSendToMeasurementCenterModalOpen}
                onHide={handleCloseSendToMeasurementCenterModal}
                centered
                backdrop={statusUpdating ? 'static' : true}
                keyboard={!statusUpdating}
            >
                <Modal.Header closeButton={!statusUpdating}>
                    <Modal.Title>Ölçme Merkezine Gönder</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className='alert alert-warning text-start'>
                        <p className='mb-2'>Sınav kitapçığı ve soruların son kontrolünü gerçekleştirdim.</p>
                        <p className='mb-2'>“Merkeze Gönder” işlemi ile birlikte sınav baskıya alınacaktır.</p>
                        <p className='mb-2'>Bu onayı vererek, baskıya gönderilen içeriklerdeki olası hata veya eksikliklerden sorumluluğun tarafıma ait olduğunu kabul ederim.</p>
                        <p className='mb-0'>Sınav baskı sürecinde Sınav Hizmetleri Birimi yalnızca teknik çoğaltma işlemini yürütmektedir.</p>
                    </div>
                    <p className='fw-semibold text-start mt-4 mb-3'>Onaylıyor musunuz?</p>
                    <div>
                        <label className='form-label fw-semibold'>Ölçme Merkezi Notu</label>
                        <textarea
                            className='form-control'
                            rows={4}
                            value={sendToMeasurementCenterNote}
                            onChange={(event) => setSendToMeasurementCenterNote(event.target.value)}
                            placeholder='Notunuzu yazınız'
                            disabled={statusUpdating}
                        />
                        <div className='form-text mt-2'>
                            Aşağıya sınavla ilgili notları yazabilirsiniz. Özellikle ortak yapılacak derslerin her birisi için ders kodu yazmayı unutmayınız.
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type='button'
                        className='btn btn-light'
                        onClick={handleCloseSendToMeasurementCenterModal}
                        disabled={statusUpdating}
                    >
                        Vazgeç
                    </button>
                    <button
                        type='button'
                        className='btn btn-primary'
                        onClick={handleConfirmSendToMeasurementCenter}
                        disabled={statusUpdating}
                    >
                        {statusUpdating ? 'Gönderiliyor...' : 'Ölçme Merkezine Gönder'}
                    </button>
                </Modal.Footer>
            </Modal>
            <div className='container py-6'>
                <div className='mb-5'>
                    <button className='btn btn-link ps-0' onClick={() => navigate(-1)}>
                        ← Geri
                    </button>
                    <h2 className='fw-bold mt-3' title={headerTitle}>
                        {headerTitle}
                    </h2>
                    {examId && examDuration !== '' && (
                        <div className='text-muted mt-2'>Sınav Süresi: {examDuration} dakika</div>
                    )}
                    {displayedGlobalExamRules.trim().length > 0 && (
                        <div className='mt-3'>
                            <div className='border rounded-3 manual-exam-global-rules-accordion'>
                                <button
                                    type='button'
                                    className='btn d-flex align-items-center justify-content-between w-100 text-start px-3 py-2'
                                    onClick={() => setIsGlobalRulesExpanded((prev) => !prev)}
                                    aria-expanded={isGlobalRulesExpanded}
                                    aria-controls={GLOBAL_RULES_CONTENT_ID}
                                >
                                    <span className='fw-semibold text-dark'>Genel Kurallar</span>
                                    <span className='ms-2 fs-4'>{isGlobalRulesExpanded ? '−' : '+'}</span>
                                </button>
                                {isGlobalRulesExpanded && (
                                    <div id={GLOBAL_RULES_CONTENT_ID} className='px-3 pb-3 bg-light manual-exam-global-rules-content'>
                                        <div
                                            className='fst-italic text-muted manual-exam-global-rules'
                                            dangerouslySetInnerHTML={{ __html: displayedGlobalExamRules }}
                                        ></div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                {!examId ? (
                    <div className='card'>
                        <div className='card-body'>
                            <form onSubmit={handleCreateExam} className='d-flex flex-column gap-4'>
                                <div>
                                    <label className='form-label fw-semibold'>Sınav Başlığı</label>
                                    <input
                                        className='form-control'
                                        value={examName}
                                        readOnly={!canEditExamName}
                                        disabled={!canEditExamName}
                                        onChange={(e) => handleExamNameChange(e.target.value)}
                                        placeholder='Sınav başlığı otomatik oluşturulur'
                                    />
                                    <div className='form-text text-muted'>
                                        Sınav başlığı ders ve sınav türüne göre otomatik oluşturulur.
                                        {canEditExamName && ' Dilerseniz güncelleyebilirsiniz.'}
                                    </div>
                                </div>
                                <div>
                                    <label className='form-label fw-semibold'>Sınav Türü</label>
                                    <select
                                        className='form-select'
                                        value={selectedExamTypeBaseKey}
                                        onChange={(e) => {
                                            const selectedGroup = groupedExamTypes.find((group) => group.key === e.target.value)
                                            if (!selectedGroup) {
                                                setSelectedExamType(null)
                                                return
                                            }

                                            setSelectedExamType(selectedGroup.options[0]?.id ?? null)
                                        }}
                                    >
                                        <option value=''>Sınav Türü Seçiniz</option>
                                        {groupedExamTypes.map((group) => (
                                            <option key={group.key} value={group.key}>
                                                {group.baseName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {selectedExamTypeGroup && selectedExamTypeGroup.options.length > 1 && (
                                    <div>
                                        <label className='form-label fw-semibold'>Sınav Sırası</label>
                                        <select
                                            className='form-select'
                                            value={selectedExamType || ''}
                                            onChange={(e) => setSelectedExamType(Number(e.target.value) || null)}
                                        >
                                            {selectedExamTypeGroup.options.map((option) => (
                                                <option key={option.id} value={option.id}>
                                                    {option.orderLabel}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                {isExamDeliveryMethodEnabled && (
                                    <div>
                                        <label className='form-label fw-semibold'>Sınavın Uygulanış Şekli</label>
                                        <select
                                            className='form-select'
                                            value={selectedExamDeliveryMethod}
                                            onChange={(e) => setSelectedExamDeliveryMethod(e.target.value as ExamDeliveryMethod)}
                                        >
                                            {EXAM_DELIVERY_METHOD_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                <div>
                                    <label className='form-label fw-semibold'>Talimatlar</label>
                                    <textarea
                                        className='form-control'
                                        rows={4}
                                        value={instructions}
                                        onChange={(e) => setInstructions(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label className='form-label fw-semibold'>Ek Materyal (PDF)</label>
                                    <input
                                        key={materialInputKey}
                                        type='file'
                                        className='form-control'
                                        accept='application/pdf'
                                        onChange={handleMaterialFileChange}
                                        disabled={materialUploading || materialDeleting}
                                    />
                                    <div className='form-text text-muted'>
                                        Sınav dışı basılacak tablo ve grafik gibi materyalleri PDF olarak ekleyebilirsiniz.
                                        Sınav oluşturulduktan sonra dosya otomatik olarak yüklenir.
                                    </div>
                                    {selectedMaterialFile && (
                                        <div className='text-muted small mt-2'>Seçili dosya: {selectedMaterialFile.name}</div>
                                    )}
                                </div>
                                <div>
                                    <label className='form-label fw-semibold'>Sınav Süresi (dakika)</label>
                                    <input
                                        type='number'
                                        className='form-control'
                                        min={0}
                                        value={examDuration}
                                        onChange={(e) => setExamDuration(e.target.value)}
                                        placeholder='Örn. 120'
                                    />
                                </div>
                                <div className='row g-4'>
                                    <div className='col-md-6'>
                                        <label className='form-label fw-semibold'>Katkı Yüzdesi</label>
                                        <input
                                            type='number'
                                            className='form-control'
                                            value={contribution}
                                            min={0}
                                            max={100}
                                            onChange={(e) => setContribution(Number(e.target.value) || 0)}
                                        />
                                    </div>
                                    <div className='col-md-6'>
                                        <label className='form-label fw-semibold'>Planlanan Soru Sayısı</label>
                                        <input
                                            type='number'
                                            className='form-control'
                                            value={plannedQuestionCount}
                                            min={0}
                                            onChange={(e) => setPlannedQuestionCount(Number(e.target.value) || 0)}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className='form-label fw-semibold'>Birleştirilecek Dersler</label>
                                    <div className='form-text text-muted'>
                                        Daha önce birleştirilen dersler otomatik olarak işaretlenmiştir. Ortak sınav
                                        yapmak istediğiniz diğer dersleri seçebilirsiniz.
                                    </div>
                                    <div className='mt-3 d-flex flex-column gap-2'>
                                        {loadingCourseShareInfo && (
                                            <div className='d-flex flex-column gap-2 text-muted small'>
                                                <div className='d-flex align-items-center gap-2'>
                                                    <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' />
                                                    <span>Ders bilgileri yükleniyor...</span>
                                                </div>
                                                {shareCourseSkeletonItems.map((index) => (
                                                    <div key={`manual-share-course-skeleton-${index}`} className='placeholder-glow'>
                                                        <span className='placeholder col-8' />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {shareCourseOptions.length > 0 ? (
                                            shareCourseOptions.map((course) => {
                                                const id = course.semesterCourseId
                                                if (typeof id !== 'number') {
                                                    return null
                                                }

                                                const checked = selectedShareCourseIds.includes(id)
                                                const disabled = Boolean(course.isLocked) || loadingCourseShareInfo || submitting

                                                return (
                                                    <label
                                                        key={`manual-share-course-${id}`}
                                                        className='form-check form-check-custom form-check-solid'
                                                    >
                                                        <input
                                                            className='form-check-input'
                                                            type='checkbox'
                                                            checked={checked}
                                                            disabled={disabled}
                                                            onChange={() => toggleSharedCourse(id)}
                                                        />
                                                        <span className='form-check-label d-flex flex-wrap align-items-center gap-2'>
                                                            <span>{formatShareCourseLabel(course)}</span>
                                                            {course.isPrimary ? (
                                                                <span className='badge badge-light-primary'>Ana Ders</span>
                                                            ) : course.isLocked ? (
                                                                <span className='badge badge-light-secondary'>Otomatik</span>
                                                            ) : null}
                                                        </span>
                                                    </label>
                                                )
                                            })
                                        ) : (
                                            !loadingCourseShareInfo && (
                                                <div className='text-muted small'>Birleştirebileceğiniz başka ders bulunmuyor.</div>
                                            )
                                        )}
                                    </div>
                                </div>
                                <div className='text-end'>
                                    <button type='submit' className='btn btn-primary' disabled={submitting}>
                                        {submitting ? 'Kaydediliyor...' : 'Sınav Oluştur'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className='card mb-5'>
                            <div className='card-body'>
                                <div className='d-flex flex-column gap-4'>
                                    {measurementCenterSubmissionNote && (
                                        <div className='alert alert-info mb-0'>
                                            <div className='fw-semibold mb-1'>Ölçme Merkezine Gönderim Notu</div>
                                            <div>{renderedSubmissionNote}</div>
                                        </div>
                                    )}
                                    {measurementCenterReturnNote && (
                                        <div className='alert alert-warning mb-0'>
                                            <div className='fw-semibold mb-1'>Ölçme Merkezi İadesi</div>
                                            <div>{renderedReturnNote}</div>
                                            <div className='text-muted small mt-2'>
                                                {measurementCenterReturnedByName && <span>İade eden: {measurementCenterReturnedByName}</span>}
                                                {formattedReturnDate && (
                                                    <span className={measurementCenterReturnedByName ? 'ms-2' : undefined}>
                                                        Tarih: {formattedReturnDate}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {sortedMeasurementCenterHistory.length > 0 && (
                                        <div className='border rounded-3 manual-exam-history-accordion'>
                                            <button
                                                type='button'
                                                className='btn d-flex align-items-center justify-content-between w-100 text-start px-3 py-2'
                                                onClick={() => setIsHistoryExpanded((prev) => !prev)}
                                                aria-expanded={isHistoryExpanded}
                                                aria-controls={HISTORY_CONTENT_ID}
                                            >
                                                <span className='fw-semibold text-dark'>İşlem Tarihçesi</span>
                                                <span className='ms-2 fs-4'>{isHistoryExpanded ? '−' : '+'}</span>
                                            </button>
                                            {isHistoryExpanded && (
                                                <div id={HISTORY_CONTENT_ID} className='px-3 pb-3 bg-light manual-exam-history-content'>
                                                    <div className='d-flex flex-column gap-4 pt-3'>
                                                        {sortedMeasurementCenterHistory.map((historyItem, index) => {
                                                            const actionType = historyItem?.actionType ?? null
                                                            const statusValue = historyItem?.status ?? null
                                                            const statusLabel = statusValue
                                                                ? MANUAL_EXAM_STATUS_LABELS[statusValue] ?? statusValue
                                                                : null
                                                            const statusBadgeClass = statusValue
                                                                ? MANUAL_EXAM_STATUS_BADGES[statusValue] ?? 'badge badge-light-primary'
                                                                : null
                                                            const historyTitle = actionType === 'StatusChange'
                                                                ? statusLabel ?? 'Durum Güncellemesi'
                                                                : actionType === 'Return'
                                                                    ? 'İade'
                                                                    : 'İşlem'
                                                            const noteLines = historyItem?.note?.split(/\r?\n/) ?? []
                                                            const actorLabel = actionType === 'Return' ? 'İade Eden' : 'İşlemi Yapan'
                                                            const fallbackNote = actionType === 'Return'
                                                                ? 'Açıklama belirtilmemiş.'
                                                                : 'İşlem açıklaması bulunmuyor.'

                                                            return (
                                                                <div
                                                                    key={`teacher-return-history-${index}`}
                                                                    className='border rounded p-3 bg-light-primary bg-opacity-25 d-flex flex-column gap-3'
                                                                >
                                                                    <div className='d-flex flex-wrap justify-content-between align-items-center gap-2'>
                                                                        <div className='fw-semibold text-gray-800'>{historyTitle}</div>
                                                                        {statusLabel && (
                                                                            <span className={`${statusBadgeClass} fw-semibold`}>{statusLabel}</span>
                                                                        )}
                                                                    </div>
                                                                    <div className='text-gray-800'>
                                                                        {noteLines.length > 0 ? (
                                                                            noteLines.map((line, lineIndex) => (
                                                                                <span key={`teacher-return-history-${index}-line-${lineIndex}`}>
                                                                                    {line}
                                                                                    {lineIndex < noteLines.length - 1 && <br />}
                                                                                </span>
                                                                            ))
                                                                        ) : (
                                                                            <span className='text-muted'>{fallbackNote}</span>
                                                                        )}
                                                                    </div>
                                                                    <div className='small text-muted d-flex flex-wrap gap-3'>
                                                                        <span>
                                                                            {actorLabel}: {historyItem?.returnedByName ?? '-'}
                                                                        </span>
                                                                        <span>Tarih: {formatHistoryDateTime(historyItem?.returnedAt)}</span>
                                                                    </div>
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {!canEditExamContent && !canManageStatus && !isPreparingStatus && (
                                        <div className='alert alert-info mb-0'>
                                            Sınav ölçme merkezine gönderildiği için düzenleme yapılamaz. PDF dosyalarını indirebilirsiniz.
                                        </div>
                                    )}
                                    {(isMeasurementCenterActive || canManageStatus || canSendToMeasurementCenter) && (
                                        <div className='d-flex flex-wrap align-items-center justify-content-between gap-3'>
                                            <div className='d-flex align-items-center gap-2'>
                                                <span className='fw-semibold'>Sınav Durumu:</span>
                                                <span className={statusBadgeClass}>{statusLabel}</span>
                                            </div>
                                            <div className='d-flex flex-wrap align-items-center gap-2'>
                                                {isMeasurementCenterActive && canSendToMeasurementCenter && examStatus === 'Preparing' && (
                                                    <button
                                                        type='button'
                                                        className='btn btn-light-success'
                                                        onClick={handleOpenSendToMeasurementCenterModal}
                                                        disabled={statusUpdating}
                                                    >
                                                        {statusUpdating ? 'Gönderiliyor...' : 'Ölçme Merkezine Gönder'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <div className='d-flex flex-wrap align-items-center justify-content-between gap-3'>
                                        <div>
                                            <h4 className='fw-bold mb-1'>Kitapçık Ayarları</h4>
                                            {lastGeneratedAt && (
                                                <div className='text-muted small'>
                                                    Son kitapçık oluşturma: {new Date(lastGeneratedAt).toLocaleString()}
                                                </div>
                                            )}
                                        </div>
                                        <div className='d-flex align-items-center gap-3 flex-wrap'>
                                            {!isBookletCountFixed && (
                                                <div className='d-flex align-items-center gap-2'>
                                                    <label className='fw-semibold mb-0'>Kitapçık Sayısı</label>
                                                    <select
                                                        className='form-select form-select-sm'
                                                        value={displayedBookletCount}
                                                        onChange={(e) => handleBookletCountChange(Number(e.target.value) || 1)}
                                                        disabled={bookletLoading || !canAdjustBookletSettings}
                                                    >
                                                        {DEFAULT_BOOKLET_CODES.map((code, index) => (
                                                            <option key={code} value={index + 1}>
                                                                {index + 1}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                            <div className='d-flex align-items-center gap-2'>
                                                <label className='fw-semibold mb-0'>Soru Boşluk Seviyesi</label>
                                                <select
                                                    className='form-select form-select-sm'
                                                    value={questionSpacingLevel}
                                                    onChange={(e) =>
                                                        setQuestionSpacingLevel(() => {
                                                            const nextValue = Number(e.target.value)
                                                            if (!Number.isFinite(nextValue)) {
                                                                return 1
                                                            }
                                                            if (nextValue < 1) {
                                                                return 1
                                                            }
                                                            if (nextValue > 5) {
                                                                return 5
                                                            }
                                                            return Math.floor(nextValue)
                                                        })
                                                    }
                                                    disabled={downloading || downloadFormat === 'sheet'}
                                                >
                                                    {[1, 2, 3, 4, 5].map((level) => (
                                                        <option key={level} value={level}>
                                                            {level}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className='d-flex align-items-center gap-2'>
                                                <label className='fw-semibold mb-0'>İndirme Formatı</label>
                                                <select
                                                    className='form-select form-select-sm'
                                                    value={downloadFormat}
                                                    onChange={(e) => {
                                                        const value = e.target.value === 'sheet' ? 'sheet' : 'booklet'
                                                        setDownloadFormat(value)
                                                    }}
                                                    disabled={bookletLoading || downloading}
                                                >
                                                    <option value='booklet'>Kitapçık Paketi</option>
                                                    <option value='sheet'>Soru Kağıdı</option>
                                                </select>
                                            </div>
                                            <div className='form-check form-switch form-check-custom form-check-solid'>
                                                <input
                                                    className='form-check-input'
                                                    type='checkbox'
                                                    id='shuffleSharedContentQuestions'
                                                    checked={shuffleSharedContentQuestions}
                                                    onChange={() => {
                                                        void handleToggleShuffleSharedContentQuestions()
                                                    }}
                                                    disabled={bookletLoading || !canAdjustBookletSettings}
                                                />
                                                <label
                                                    className='form-check-label fw-semibold'
                                                    htmlFor='shuffleSharedContentQuestions'
                                                >
                                                    Senaryo sorularının sırasını kitapçıklarda karıştır
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='d-flex flex-wrap gap-3'>
                                        <button
                                            type='button'
                                            className='btn btn-light-primary'
                                            onClick={handleGenerateBooklets}
                                            disabled={bookletLoading || submitting || !canAdjustBookletSettings || !canEditExamContent}
                                        >
                                            {bookletLoading ? 'İşleniyor...' : 'Kitapçıkları Oluştur'}
                                        </button>
                                        <button
                                            type='button'
                                            className='btn btn-light'
                                            onClick={handleShowAnswerKeys}
                                            disabled={bookletLoading}
                                        >
                                            Cevap Anahtarını Göster
                                        </button>
                                        <button
                                            type='button'
                                            className='btn btn-primary'
                                            onClick={handleDownloadPdf}
                                            disabled={downloading}
                                        >
                                            {downloading ? 'İndiriliyor...' : downloadButtonLabel}
                                        </button>
                                    </div>
                                    <div className='mt-5'>
                                        <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3'>
                                            <h5 className='fw-semibold mb-0'>Kitapçık Tasarım Ayarları</h5>
                                            <div className='d-flex flex-wrap align-items-center gap-2'>
                                                {isDynamicLayoutEnabled && defaultLayoutSettings && (
                                                    <button
                                                        type='button'
                                                        className='btn btn-sm btn-light'
                                                        onClick={handleApplyDefaultLayout}
                                                        disabled={!canAdjustLayoutSettings}
                                                    >
                                                        Varsayılanı uygula
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        {isDynamicLayoutEnabled && (
                                            <div className='row g-4'>
                                                <div className='col-12'>
                                                    <ManualExamLayoutSettingsEditor
                                                        value={layoutSettings}
                                                        onChange={(next) =>
                                                            setLayoutSettings(normalizeManualExamLayoutSettings(next))
                                                        }
                                                        disabled={!canAdjustLayoutSettings}
                                                    />
                                                </div>
                                                <div className='col-12'>
                                                    <ManualExamLayoutPreview
                                                        value={layoutSettings}
                                                        className='h-100'
                                                        title='Önizleme'
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {questionSummary.total > 0 && (
                                        <div className='mt-5'>
                                            <div className='border rounded-3 p-4 bg-light'>
                                                <h5 className='fw-semibold mb-3'>Soru Özeti</h5>
                                                {questionSummary.difficulty.length > 0 && (
                                                    <div className='mb-4'>
                                                        <div className='fw-semibold mb-2'>Zorluk Düzeyi Dağılımı</div>
                                                        <div className='d-flex flex-wrap gap-2'>
                                                            {questionSummary.difficulty.map((item) => (
                                                                <span key={`difficulty-${item.label}`} className='badge badge-light-primary'>
                                                                    {item.label}: {item.count}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {questionSummary.outcomes.length > 0 && (
                                                    <div>
                                                        <div className='fw-semibold mb-2'>
                                                            {isBoardCourse ? 'Birime Göre Dağılım' : 'Öğrenme Çıktısı Dağılımı'}
                                                        </div>
                                                        <div className='d-flex flex-column gap-2'>
                                                            {questionSummary.outcomes.map((item) => (
                                                                <div
                                                                    key={`outcome-${item.label}`}
                                                                    className='d-flex justify-content-between align-items-center gap-3'
                                                                >
                                                                    <span className='flex-grow-1 fw-semibold text-dark'>{item.label}</span>
                                                                    <span className='badge bg-primary text-white fw-semibold px-3 py-2'>
                                                                        {item.count}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className='card mb-5'>
                            <div className='card-body d-flex flex-column gap-3'>
                                <div>
                                    <div className='fw-semibold'>Ek Materyal (PDF)</div>
                                    <div className='text-muted small'>
                                        Sınav dışı basılacak tablolar, grafikler ve benzeri yardımcı materyaller için PDF
                                        yükleyebilirsiniz.
                                    </div>
                                </div>
                                {supplementaryMaterialPath ? (
                                    <div className='d-flex flex-wrap align-items-center gap-3'>
                                        <a
                                            href={materialDownloadUrl || '#'}
                                            target='_blank'
                                            rel='noreferrer'
                                            className='fw-semibold text-primary text-decoration-underline'
                                        >
                                            {supplementaryMaterialFileName || 'Yüklü PDF'}
                                        </a>
                                        <span className='badge badge-light-success'>Hazır</span>
                                        <button
                                            type='button'
                                            className='btn btn-light-danger btn-sm'
                                            onClick={handleDeleteMaterial}
                                            disabled={materialDeleting || materialUploading}
                                        >
                                            {materialDeleting ? 'Kaldırılıyor...' : "PDF'i Kaldır"}
                                        </button>
                                    </div>
                                ) : (
                                    <div className='text-muted'>Henüz bir materyal yüklenmedi.</div>
                                )}
                                <div className='d-flex flex-wrap align-items-center gap-3'>
                                    <input
                                        key={materialInputKey}
                                        type='file'
                                        accept='application/pdf'
                                        className='form-control w-auto'
                                        onChange={handleMaterialFileChange}
                                        disabled={materialUploading || materialDeleting}
                                    />
                                    <button
                                        type='button'
                                        className='btn btn-light-primary'
                                        onClick={handleUploadMaterial}
                                        disabled={!selectedMaterialFile || materialUploading || materialDeleting}
                                    >
                                        {materialUploading ? 'Yükleniyor...' : 'PDF Yükle'}
                                    </button>
                                    {selectedMaterialFile && (
                                        <span className='text-muted small'>{selectedMaterialFile.name}</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {Object.keys(answerKeys).length > 0 && (
                            <div className='card mb-5'>
                                <div className='card-body'>
                                    <div className='d-flex justify-content-between align-items-center mb-3'>
                                        <h5 className='fw-bold mb-0'>Cevap Anahtarları</h5>
                                        {answerKeyGeneratedAt && (
                                            <span className='text-muted small'>Hazırlandığı tarih: {new Date(answerKeyGeneratedAt).toLocaleString()}</span>
                                        )}
                                    </div>
                                    <div className='row g-4'>
                                        {Object.entries(answerKeys).map(([code, items]) => (
                                            <div key={code} className='col-md-6'>
                                                <h6 className='fw-semibold mb-2'>Kitapçık {code}</h6>
                                                <div className='table-responsive'>
                                                    <table className='table table-sm table-bordered align-middle mb-0'>
                                                        <thead>
                                                            <tr>
                                                                <th style={{ width: '30%' }}>Soru</th>
                                                                <th>Doğru Şık</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {items.map((item) => (
                                                                <tr key={`${code}-${item.examQuestionId}`}>
                                                                    <td>{item.questionNumber}</td>
                                                                    <td>{item.letter || '-'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {previewContent && !isCompactViewport && (
                            <div className='card mb-5'>
                                <div className='card-header'>
                                    <h5 className='card-title mb-0'>Soru Önizleme</h5>
                                </div>
                                <div className='card-body'>{previewContent}</div>
                            </div>
                        )}

                    </>
                )}


                <Modal
                    show={isPreviewModalOpen && Boolean(previewContent)}
                    onHide={() => setIsPreviewModalOpen(false)}
                    centered
                    size='lg'
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Soru Önizleme</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>{previewContent}</Modal.Body>
                </Modal>

                <div className='row g-5'>
                    <div className='col-lg-6'>
                        <div className='d-flex align-items-center justify-content-between mb-3'>
                            <div className='d-flex align-items-center gap-3'>
                                <h4 className='fw-bold mb-0'>Soru Havuzu</h4>
                                <span className='badge badge-light-primary'>Toplam {questionPoolQuestionCount} soru</span>
                                <button
                                    type='button'
                                    className='btn btn-sm btn-light-primary'
                                    onClick={handleOpenAutoAddModal}
                                    disabled={!examId || !canEditExamContent || autoAddSubmitting}
                                >
                                    Otomatik Soru Ekle
                                </button>
                            </div>
                            <div className='d-flex align-items-center gap-2'>
                                <span className='text-muted small'>
                                    {isBoardCourse ? 'Birim' : 'Ders Öğrenme Çıktısı'}
                                </span>
                                {departments.length ? (
                                    <select
                                        className='form-select form-select-sm'
                                        style={{ maxWidth: '260px' }}
                                        value={selectedDepartmentId ?? ''}
                                        onChange={(e) => {
                                            const { value } = e.target
                                            setSelectedDepartmentId(value === '' ? null : Number(value))
                                        }}
                                    >
                                        {departments.map((dept) => {
                                            const label = dept.name || (isBoardCourse ? 'İsimsiz Birim' : 'İsimsiz Çıktı')
                                            return (
                                                <option key={dept.id} value={dept.id} title={label}>
                                                    {label}
                                                </option>
                                            )
                                        })}
                                    </select>
                                ) : (
                                    <span className='text-muted small'>
                                        İlişkili {isBoardCourse ? 'birim' : 'ders öğrenme çıktısı'} bulunamadı.
                                    </span>
                                )}
                            </div>
                        </div>
                        {loading && <div className='alert alert-light border'>Sorular yükleniyor...</div>}
                        {!loading && renderQuestionPool()}
                    </div>
                    <div className='col-lg-6'>
                        <div className='d-flex align-items-center justify-content-between mb-3'>
                            <h4 className='fw-bold mb-0'>Sınavdaki Sorular</h4>
                            <span className='badge badge-light-primary'>{assignments?.questionCount || 0} soru</span>
                        </div>
                        {renderAssignments()}
                    </div>
                </div>
            </div>
        </>
    )
}

export default ManualExamBuilder
